/**
 * Removes draft versions for a specific major version
 * Parity with legacy ApiFiles.removeDraftVersions
 * @param fileId - File system ID
 * @param versionId - Major version ID
 * @param validation - E-signature validation object (optional)
 * @returns Promise resolving when draft versions are removed
 */
export async function removeDraftVersions(
  fileId: number,
  versionId: number,
  validation?: Record<string, any>
): Promise<void> {
  const requestObj: Record<string, any> = {}

  if (validation && typeof validation === 'object') {
    Object.assign(requestObj, validation)
  }

  return baseApiService.fetch<void>(`/api/files/${fileId}/purgeminorversions?versionId=${versionId}`, {
    method: 'PATCH',
    body: requestObj
  })
}

/**
 * Downloads a zip of selected files as PDFs.
 * Parity with legacy ApiFiles.getFilesPDFDownloadUrl
 * @param selectedIds - Array of file IDs to download
 * @param esignature - E-signature payload (optional)
 * @param options - Additional options (staging area, etc)
 * @returns Promise resolving to download info
 */
export async function getFilesPDFDownloadUrl(
  selectedIds: number[],
  esignature?: Record<string, any>,
  options: Record<string, any> = {}
): Promise<{ url?: string; s3Url?: string; message?: string }> {
  // Strict parity with legacy logic
  // options.selectedWholeResult branch
  if (options && options.selectedWholeResult) {
    // Properties search branch
    if (options.isPropertiesSearch && options.filter && options.filter.metadataCriteria) {
      const filter = { ...options.filter, workflowIsNull: true }
      return baseApiService.fetch<{ url?: string; s3Url?: string; message?: string }>(
        '/api/files/propertysearch/downloadall',
        { method: 'PATCH', body: { filter } }
      )
    }
    // Exported reports branch
    else if (options.exportedReports) {
      const filter = { ...options.filter }
      filter.pageSize = undefined
      filter.pageStart = undefined
      filter.sortBy = undefined
      filter.sortDesc = undefined
      return baseApiService.fetch<{ url?: string; s3Url?: string; message?: string }>(
        '/api/files/reports/downloadall',
        { method: 'PATCH', body: filter }
      )
    }
    // Parent folder branch
    else if (options.parentFolderId) {
      const requestObj: Record<string, any> = { esignature }
      if (options.filter) {
        Object.assign(requestObj, options.filter)
      }
      return baseApiService.fetch<{ url?: string; s3Url?: string; message?: string }>(
        `/api/files/${options.parentFolderId}/downloadall`,
        { method: 'PATCH', body: requestObj }
      )
    }
  }
  // Default: multiDownload branch
  const requestObj: Record<string, any> = { ids: selectedIds, esignature }
  return baseApiService.fetch<{ url?: string; s3Url?: string; message?: string }>('/api/files/multiDownload', {
    method: 'PATCH',
    body: requestObj
  })
}
import { baseApiService, ApiError, type QueryParams } from './base'
import type { RecentDocumentsResponse, FavoritesResponse } from './types'
import type { FilesResponse } from '../../types/api'
import { getTenantUuid, getUser } from '../auth'
import { authApiService } from './auth'
import { usersApiService } from './users'
import { zipFilesAsync, type ZipResult } from './files-upload'
import type { User, Group } from './users'
import _ from 'lodash'
import { toast } from '../toast'
import checkmateService from '../checkmate'
import { getUrlParam } from '../../helpers/generalHelper'

/**
 * Interface for file trainees API response
 */
interface FileTraineesResponse {
  trainees: {
    type: string
    steps: Array<{
      processingType: string
      taskTeamStepLabel: string
      nofAllValue: number
      taskTeams: Array<{
        group?: { id: number }
        user?: { id: number }
      }>
    }>
  }
  readOnly: boolean
  workflowTemplate: any
}

/**
 * Interface for processed file trainees with merged user/group data
 */
interface FileTraineesProcessed {
  trainees: {
    createRAActionType: string
    type: string
    steps: Array<{
      processingType: string
      taskTeamStepLabel: string
      nofAllValue: number
      taskTeams: Array<{
        group?: { id: number }
        user?: { id: number }
      }>
      members?: Array<{
        id: number
        systemHelperType: 'USER' | 'GROUP'
      }>
    }>
  }
  readOnly: boolean
  workflowTemplate: any
}

// Type for the response (should be refined based on actual API response)
export interface MimeTypeCategory {
  id: number
  name: string
  count?: number
}

export interface MimeTypeCategoriesResponse {
  elements: MimeTypeCategory[]
}

/**
 * Interface for folder structure parameter
 */
export interface FolderStructureParam {
  name: string
  isArray: boolean
  values: string[]
  error?: boolean
}

/**
 * Response from getFolderStructureParams API
 */
export interface FolderStructureParamsResponse {
  params: FolderStructureParam[]
}

/**
 * Request body for createFolderStructure API
 */
export interface CreateFolderStructureRequest {
  parentFolderId: number
  folderStructureTemplateId: number
  params: FolderStructureParam[]
  esignature?: any
  [key: string]: unknown
}

export interface FileDetail {
  id: number
  name: string
  systemFolderType: string
  type: string
  obsolete: boolean
  isBlindedDocument: boolean
  createdAt: number
  updatedOn: number
  title: string
  docNo: number
  documentUID: number
  valid: boolean
  certifiedCopy: boolean
  uuid: string
  acl: string
  parent: {
    id: number
    name: string
  }
  fsiTypeTemplate: {
    id: number
    name: string
  }
  owner: {
    id: number
    name: string
  }
  createdBy: {
    id: number
    name: string
  }
  updatedBy: {
    id: number
    name: string
  }
  qcReviewStatus: string
  isConvertable: boolean
  versionNo: string
  mimetype: string
  size: number
  state: string
  actionFlags: {
    canObsolete: boolean
    canUnobsolete: boolean
    canRename: boolean
    canCopy: boolean
    canMove: boolean
    canChangeDocType: boolean
    canChangeOwner: boolean
    canChangeExpiration: boolean
    canChangeBlinded: boolean
    canPermanentlyDelete: boolean
    canReject: boolean
    canFileToEtmf: boolean
    canDownloadZip: boolean
    canShowReferences: boolean
    canManageReferences: boolean
    canShowAcl: boolean
    canManageAcl: boolean
    canValidate: boolean
    canDownloadSource: boolean
    canRegenerate: boolean
    canReclassify: boolean
    canCreateChangeRequestsFromPlaceholders: boolean
    canEditDetails: boolean
    canAddFolder: boolean
    canDoMigration: boolean
    canExternalAndUserDoSimpleMigration: boolean
    canAddFile: boolean
    canDoExport: boolean
    canManageNotifications: boolean
    canManageCR: boolean
    canRequestChange: boolean
    canRequestRemoval: boolean
    canShareLink: boolean
    canShowVersions: boolean
    canShowProperties: boolean
    canShowActivity: boolean
    canAddAmendment: boolean
    canEditBlinded: boolean
    canEditFSTType: boolean
    canRemoveDocFromSupGroup: boolean
    noCRAuthorizers: boolean
    canBeTargetInFileArtifactWorkflow: boolean
    canManageTags: boolean
    canShowTags: boolean
    canCreateQCReview: boolean
    eosSiteAndCanBeRenamed: boolean
    beforeApproveStep: boolean
  }
  thumbnailId: string
  versionId: number
  minorVersionId: number
  templateAllowed: boolean
  lastApprovers: unknown[]
  requireExpirationDate: boolean
  expired: boolean
  systemFolderTypeLocalized: string
  folderStructureSubFolder: boolean
  folderPathText?: string
  isFavorite?: boolean
  favoriteName?: string
}

// Interface for the template parameter used in updateFsiRejectionReviewersV2
export interface FsiRejectionReviewersTemplate {
  rejectionReviewers: {
    usersGroups: string[] // Array of strings in format "USER-<id>" or "GROUP-<id>"
    type: string
  }
}

// Remediate a file for periodic review
export interface RemediateFilesRequest {
  esignature?: Record<string, unknown>
}

export interface PathItem {
  id: number
  name: string
  acl: string
  aclLevel?: string // Mapped from acl for compatibility with Breadcrumbs
  app?: string
  mccObjUuid?: string
  mccObjType?: string
}

export interface AwsUploadUrlResponse {
  url: string
}

export interface S3UploadResult {
  success: boolean
  uuid?: string
  error?: string
}

export interface S3UploadOptions {
  onProgress?: (percentage: number) => void
  abortSignal?: AbortSignal
  compressionLevel?: number
}

// Workflow participant types
export interface TaskTeam {
  group?: { id: number }
  user?: { id: number }
}

export interface WorkflowStep {
  processingType: string
  taskTeamStepLabel: string
  nofAllValue: string
  taskTeams: TaskTeam[]
  merged?: Array<{ id: number; systemHelperType: string }>
}

export interface ApproveAction {
  '@type': string
  actionData?: string
  notifies: Array<{ group?: { id: number }; user?: { id: number } }>
  moveToFsiId?: number
  copyToFsiId?: number
  customNotificationText?: string
}

export interface ApproveActionProcessed {
  actionType: string
  actionData?: string
  notifies: Array<{ group?: { id: number }; user?: { id: number } }>
  merged: Array<{ id: number; systemHelperType: string }>
  selectedFolder?: number
}

export interface FileApproversResponse {
  approvers: {
    type: string
    steps: WorkflowStep[]
    approveActions?: ApproveAction[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileApproversProcessed {
  approvers: {
    type: string
    steps: WorkflowStep[]
    approveActions: ApproveActionProcessed[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileReviewersResponse {
  reviewers: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileReviewersProcessed {
  reviewers: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileEditorsResponse {
  collaborators: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileEditorsProcessed {
  collaborators: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileCRApproversResponse {
  initialApprovers: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export interface FileCRApproversProcessed {
  initialApprovers: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

export type FileItem = {
  id: number
  name: string
  createdAt: number
  updatedAt: number
}

export async function getFiles(parentId: number, filter?: Record<string, unknown>): Promise<FilesResponse> {
  if (filter) {
    // Clean up the filter object - remove undefined/null/empty values
    const cleanFilter = Object.entries(filter).reduce<Record<string, string | number | boolean | string[] | number[]>>(
      (acc, [key, value]) => {
        // Skip undefined, null, empty string
        if (value === undefined || value === null || value === '') {
          return acc
        }

        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0) {
          return acc
        }

        // Keep only valid primitive or array values supported by QueryParams
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          acc[key] = value
        } else if (Array.isArray(value)) {
          const arr = value.filter((v) => typeof v === 'string' || typeof v === 'number') as Array<string | number>
          // Only assign non-empty arrays of allowed primitives
          if (arr.length > 0) {
            // Narrow to string[] or number[] as best as we can
            const isStringArray = arr.every((v) => typeof v === 'string')
            acc[key] = isStringArray ? (arr as string[]) : (arr as number[])
          }
        }
        return acc
      },
      {}
    )

    // Use /api/v2/files endpoint which supports filtering (same as AngularJS getList)
    return baseApiService.fetch<FilesResponse>(`/api/v2/files/${parentId}`, { params: cleanFilter })
  }

  // Use /api/files endpoint
  return baseApiService.fetch<FilesResponse>(`/api/files/${parentId}`)
}

export async function getFilePath(fileId: number): Promise<PathItem[]> {
  const path = await baseApiService.fetch<PathItem[]>(`/api/v2/files/${fileId}/path`)
  // Map acl to aclLevel for compatibility with Breadcrumbs (same as legacy getFsiPath)
  path.forEach((item) => {
    item.aclLevel = item.acl
  })
  return path
}

export async function getFileDetail(
  fileId: number,
  versionId?: number | null,
  minorVersionId?: number | null
): Promise<FileDetail> {
  return getDetails(fileId, versionId, minorVersionId)
}

/**
 * Get file details with optional version parameters
 * @param fileId - File ID
 * @param versionId - Optional version ID
 * @param minorVersionId - Optional minor version ID
 * @param hideLoader - Whether to hide the loading indicator
 */
export async function getDetails(
  fileId: number,
  versionId?: number | null,
  minorVersionId?: number | null,
  hideLoader?: boolean
): Promise<FileDetail> {
  const params: Record<string, number> = {}

  // Only add versionId if it's a valid number >= 0
  if (typeof versionId === 'number' && versionId >= 0) {
    params.versionId = versionId
  }

  // Only add minorVersionId if we have a valid versionId and minorVersionId
  if (typeof params.versionId === 'number' && typeof minorVersionId === 'number') {
    params.minorVersionId = minorVersionId
  }

  const data = await baseApiService.fetch<FileDetail>(`/api/v2/files/${fileId}/detail`, {
    params
  })

  // Transform favorite fields to match legacy behavior
  data.isFavorite = !!data.favoriteName
  if (!data.favoriteName) {
    data.favoriteName = data.name
  }

  return data
}

export async function getFileV1(fileId: number): Promise<{ name: string }> {
  const params: Record<string, number> = {
    pageSize: 1
  }
  return baseApiService.fetch<{ name: string }>(`/api/files/${fileId}`, {
    params
  })
}

export async function getFile(fileId: number): Promise<FileItem> {
  const params: Record<string, number> = {
    pageSize: 1
  }
  return baseApiService.fetch<FileItem>(`/api/files/${fileId}`, {
    params
  })
}

/**
 * Get number of documents in folder and subfolders
 * @param fileId - Folder ID
 * @param filters - Optional filter object
 */
export async function getNumbersofdocs(
  fileId: number,
  filters?: any
): Promise<{
  numberOfEffectiveDocumentsInFolder: number
  numberOfEffectiveDocumentsInSubTree: number
  sizeofDocumentsInFolder: number
  sizeofDocumentsInSubTree: number
  sumOfDocumentsWithoutWorkflowInFolder?: number
}> {
  return baseApiService.fetch(`/api/files/numbersofdocs/${fileId}`, {
    params: filters
  })
}

/**
 * Get FSI path with ACL levels
 * @param fsiId - File/folder ID
 * @param removeName - Remove the last element (the node itself) from path
 */
export async function getFsiPath(fsiId: number, removeName?: boolean): Promise<PathItem[]> {
  const path = await baseApiService.fetch<PathItem[]>(`/api/v2/files/${fsiId}/path`)

  // Map acl to aclLevel for compatibility with Breadcrumbs (same as legacy getFsiPath)
  path.forEach((item) => {
    item.aclLevel = item.acl
  })

  if (removeName && path.length > 0) {
    return path.slice(0, -1)
  }

  return path
}

export interface FilepickerFilter {
  obsolete?: boolean
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | undefined
}

export interface FilepickerOptions {
  onlyFoldersAndDocuments?: boolean
  onlyFoldersAndPlaceholders?: boolean
  onlyFoldersAndDocumentsWithWriteAccess?: boolean
  onlyFolders?: boolean
  [key: string]: string | number | boolean | undefined
}

export interface FilepickerNode {
  id: number
  name: string
  fileName?: string
  type: string
  systemFolderType?: string
  parentId?: number
  templateId?: number
  hasChildren?: boolean
  acl?: string
  [key: string]: string | number | boolean | undefined
}

/**
 * Get single file/folder node for file picker with minimal info
 * @param id - File/folder ID
 * @param filter - Filter parameters
 * @param options - Picker options
 */
export async function getFilepickerFsi(
  id: number,
  filter?: FilepickerFilter,
  options?: FilepickerOptions
): Promise<FilepickerNode> {
  const params = {
    ...filter,
    ...options
  }

  return baseApiService.fetch<FilepickerNode>(`/api/files/${id}/filepickersinglenode`, {
    params
  })
}

/**
 * Get list of child nodes for file picker
 * @param parentId - Parent folder ID
 * @param filter - Filter parameters (obsolete, sorting)
 * @param options - Picker options (onlyFolders, etc.)
 */
export async function getFsisFilepicker(
  parentId: number,
  filter?: FilepickerFilter,
  options?: FilepickerOptions
): Promise<FilepickerNode[]> {
  const params = {
    ...filter,
    ...options
  }

  return baseApiService.fetch<FilepickerNode[]>(`/api/files/${parentId}/filepickerlist`, {
    params
  })
}

export async function getRecentDocuments(): Promise<RecentDocumentsResponse> {
  return baseApiService.fetch<RecentDocumentsResponse>('/api/v2/files/recents', {
    params: { pageSize: 5, pageStart: 0 }
  })
}

export interface RecentDocumentsParams {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | undefined
}

export async function getRecentDocumentsFiltered(params: RecentDocumentsParams): Promise<RecentDocumentsResponse> {
  return baseApiService.fetch<RecentDocumentsResponse>('/api/v2/files/recents', {
    params: {
      pageSize: params.pageSize || 5,
      pageStart: params.pageStart || 0,
      sortBy: params.sortBy || 'updatedAt',
      sortDesc: params.sortDesc || true,
      ...params
    }
  })
}

export async function validateFile(fileId: number): Promise<{ value: boolean }> {
  return baseApiService.fetch<{ value: boolean }>(`/api/files/${fileId}/validate`)
}

export async function getDownloadUrl(
  fileId: number
): Promise<{ url: string; s3Url?: string; message?: string; uuidToCheckState: string }> {
  return baseApiService.fetch(`/api/files/${fileId}/convert/watermarkedpdf`, {
    params: { forDownload: true }
  })
}

/**
 * Get download URL for original file (preserves original format, not converted to PDF)
 * Used for ACTION_COMMAND_DOWNLOAD_DOCUMENT in tasks
 */
export async function getFileDownloadUrl(fileId: number): Promise<{ url: string; s3Url?: string; message?: string }> {
  return baseApiService.fetch(`/api/files/${fileId}/contenturl`)
}
interface FavoritesParams {
  pageSize: number
  pageStart: number
  sortBy: string
  sortDesc: boolean
  name?: string
  [key: string]: string | number | boolean | undefined
}

export type { FavoritesParams }

export interface FileProperties {
  properties: Record<string, unknown>
}

export async function getFavorites(params: FavoritesParams): Promise<FavoritesResponse> {
  return baseApiService.fetch<FavoritesResponse>('/api/files/favorites', {
    params
  })
}

export async function getFileProperties(fileId: number): Promise<FileProperties> {
  return baseApiService.fetch<FileProperties>(`/api/files/${fileId}/properties`)
}

export interface MakeObsoleteRequest {
  esignature: Record<string, unknown>
  [key: string]: unknown
}

export async function makeFileObsolete(fileId: number, request: MakeObsoleteRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/delete`, {
    method: 'PATCH',
    body: request
  })
}

export interface MakeMultipleObsoleteRequest {
  ids: number[]
  esignature: Record<string, unknown>
  [key: string]: unknown
}

export async function makeMultipleFilesObsolete(request: MakeMultipleObsoleteRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multiObsolete', {
    method: 'PATCH',
    body: request
  })
}

export interface ObsoleteAllInFolderRequest {
  esignatureDTO: Record<string, unknown>
  filter: Record<string, unknown>
  [key: string]: unknown
}

export async function obsoleteAllFilesInFolder(folderId: number, request: ObsoleteAllInFolderRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${folderId}/obsoleteall`, {
    method: 'PATCH',
    body: request
  })
}

export interface ObsoleteAllFromPropertiesSearchRequest {
  propertySearchRequestDTO: {
    filter: Record<string, unknown>
  }
  esignatureDTO: Record<string, unknown>
  [key: string]: unknown
}

export async function obsoleteAllFilesFromPropertiesSearch(
  request: ObsoleteAllFromPropertiesSearchRequest
): Promise<void> {
  return baseApiService.fetch<void>('/api/files/propertysearch/obsoleteall', {
    method: 'PATCH',
    body: request
  })
}

export interface MakeMultipleUnobsoleteRequest {
  ids: number[]
  esignature: Record<string, unknown>
  [key: string]: unknown
}

export async function makeMultipleFilesUnobsolete(request: MakeMultipleUnobsoleteRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multiUnobsolete', {
    method: 'PATCH',
    body: request
  })
}

export interface UnobsoleteAllInFolderRequest {
  esignatureDTO: Record<string, unknown>
  filter: Record<string, unknown>
  [key: string]: unknown
}

export async function unobsoleteAllFilesInFolder(
  parentFolderId: number,
  request: UnobsoleteAllInFolderRequest
): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${parentFolderId}/unobsoleteall`, {
    method: 'PATCH',
    body: request
  })
}

export interface UnobsoleteAllFromPropertiesSearchRequest {
  propertySearchRequestDTO: {
    filter: Record<string, unknown>
  }
  esignatureDTO: Record<string, unknown>
  [key: string]: unknown
}

export async function unobsoleteAllFilesFromPropertiesSearch(
  request: UnobsoleteAllFromPropertiesSearchRequest
): Promise<void> {
  return baseApiService.fetch<void>('/api/files/propertysearch/unobsoleteall', {
    method: 'PATCH',
    body: request
  })
}

export interface FileContentUrlResponse {
  url: string
  message: string | null
}

export async function getFileContentUrl(fileId: number): Promise<FileContentUrlResponse> {
  return baseApiService.fetch<FileContentUrlResponse>(`/api/files/${fileId}/contenturl`)
}

export async function getAwsUploadUrl(uuid: string): Promise<AwsUploadUrlResponse> {
  return baseApiService.fetch<AwsUploadUrlResponse>(
    `/api/integrations/aws/storage/presignedurl/write/migration/${uuid}`
  )
}

// Direct port from Angular persistService.generateUuid()
export function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

async function uploadToS3(presignedUrl: string, file: Blob, options: S3UploadOptions = {}): Promise<void> {
  const { onProgress, abortSignal } = options

  console.log('[uploadToS3] Starting S3 upload...')
  console.log('[uploadToS3] Presigned URL:', presignedUrl)
  console.log('[uploadToS3] Blob details:', {
    size: file.size,
    type: file.type,
    isBlob: file instanceof Blob
  })

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        console.log('[uploadToS3] Upload aborted by signal')
        xhr.abort()
        reject(new DOMException('Upload aborted', 'AbortError'))
      })
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = (event.loaded / event.total) * 100
        console.log(
          '[uploadToS3] Upload progress:',
          progress.toFixed(2) + '%',
          `(${event.loaded}/${event.total} bytes)`
        )
        onProgress(progress)
      }
    }

    xhr.onload = () => {
      console.log('[uploadToS3] Request completed. Status:', xhr.status)
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('[uploadToS3] S3 upload successful!')
        resolve()
      } else {
        console.error('[uploadToS3] S3 upload failed with status', xhr.status, 'Response:', xhr.responseText)
        reject(new Error(`S3 upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      console.error('[uploadToS3] XHR error occurred')
      reject(new Error('S3 upload failed'))
    }

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    console.log('[uploadToS3] Sending blob to S3... Size:', file.size, 'bytes')
    xhr.send(file)
  })
}

async function compressFiles(
  files: File[],
  options: S3UploadOptions = {}
): Promise<{ blob: Blob; isCompressed: boolean }> {
  const { onProgress } = options

  // Use zipFilesAsync from files-upload.ts for proper multi-file compression
  // This handles:
  // - Single ZIP file detection (returns as-is)
  // - Multiple files compression using zip.js
  // - Progress tracking during compression
  const result = await zipFilesAsync(files, onProgress)

  return {
    blob: result.blob,
    isCompressed: result.isZip ?? false
  }
}

function createProgressUpdater(
  onProgress?: (percentage: number) => void
): (percentage: number, phaseStart?: number, phaseEnd?: number) => void {
  return (percentage: number, phaseStart = 0, phaseEnd = 100) => {
    if (!onProgress) return

    const currentPhaseLength = phaseEnd - phaseStart
    const totalProgress = phaseStart + (percentage / 100) * currentPhaseLength
    onProgress(Math.min(100, Math.max(0, totalProgress)))
  }
}

async function handleCompressionPhase(
  files: File[],
  options: S3UploadOptions,
  updateProgress: ReturnType<typeof createProgressUpdater>
): Promise<{ blob: Blob; isCompressed: boolean }> {
  return await compressFiles(files, {
    onProgress: (p) => updateProgress(p, 0, 60)
  })
}

async function handleS3UploadPhase(
  blob: Blob,
  uuid: string,
  options: S3UploadOptions,
  updateProgress: ReturnType<typeof createProgressUpdater>
): Promise<void> {
  console.log('[handleS3UploadPhase] Getting AWS upload URL for uuid:', uuid)
  console.log('[handleS3UploadPhase] Blob size:', blob.size, 'Type:', blob.type)

  const urlResponse = await getAwsUploadUrl(uuid)
  console.log('[handleS3UploadPhase] Got presigned URL:', urlResponse.url)

  await uploadToS3(urlResponse.url, blob, {
    onProgress: (p) => updateProgress(p, 60, 100),
    abortSignal: options.abortSignal
  })

  console.log('[handleS3UploadPhase] S3 upload phase complete')
}

export async function uploadFilesToS3(files: File[], options: S3UploadOptions = {}): Promise<S3UploadResult> {
  try {
    const { abortSignal } = options
    let uploadAborted = false

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        uploadAborted = true
      })
    }

    const updateProgress = createProgressUpdater(options.onProgress)

    // Phase 1: File compression
    const compressionResult = await handleCompressionPhase(files, options, updateProgress)

    if (uploadAborted) {
      return { success: false, error: 'Upload aborted' }
    }

    // Phase 2: S3 upload
    const uuid = generateUuid()
    await handleS3UploadPhase(compressionResult.blob, uuid, options, updateProgress)

    if (uploadAborted) {
      return { success: false, error: 'Upload aborted' }
    }

    return { success: true, uuid }
  } catch (error) {
    console.error('S3 upload failed:', error)

    if (error instanceof DOMException && error.name === 'AbortError') {
      return { success: false, error: 'Upload aborted' }
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'S3 upload failed'
    }
  }
}

// Additional file management functions from api.files.js

export interface FileVersions {
  versions: Array<{
    id: number
    versionNo: string
    minorVersionId: number
    effectiveDate: number
    createdBy: {
      id: number
      name: string
    }
    size: number
  }>
}

export interface FileValidation {
  value: boolean
  message?: string
}

export interface ShareLinkResponse {
  shareLink: string
}

export async function getFileVersions(fileId: number): Promise<FileVersions> {
  return baseApiService.fetch<FileVersions>(`/api/files/${fileId}/versions`)
}

/**
 * Revert file to a specific version
 * Restores a file to a previous version with optional e-signature validation
 * @param fileId - The ID of the file
 * @param versionId - The version ID to revert to
 * @param validation - Optional e-signature validation object
 * @returns Promise that resolves when revert is complete
 */
export async function revertFileToVersion(
  fileId: number,
  versionId: number,
  validation?: Record<string, any>
): Promise<void> {
  const requestObj: Record<string, any> = {}
  if (validation && typeof validation === 'object') {
    Object.assign(requestObj, validation)
  }
  return baseApiService.fetch<void>(`/api/files/${fileId}/${versionId}/reverttoversion`, {
    method: 'PATCH',
    body: requestObj
  })
}

export async function getFileIdByUuid(fileUuid: string): Promise<{ id: number }> {
  return baseApiService.fetch<{ id: number }>(`/api/files/uuid/${fileUuid}`)
}

/**
 * Get folder structure template parameters
 * Retrieves dynamic parameter fields for a selected folder structure template
 * @param templateId - The ID of the folder structure template
 * @param filter - Optional filter parameters
 * @returns Promise with folder structure parameters
 */
export async function getFolderStructureParams(
  templateId: number,
  filter?: QueryParams
): Promise<FolderStructureParamsResponse> {
  return baseApiService.fetch<FolderStructureParamsResponse>(`/api/files/folderstructures/${templateId}/params`, {
    params: filter
  })
}

/**
 * Create a folder structure from a template
 * Creates a new folder structure based on a template with dynamic parameters
 * @param request - The folder structure creation request
 * @returns Promise that resolves when folder structure is created
 */
export async function createFolderStructure(request: CreateFolderStructureRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/folderstructures', {
    method: 'POST',
    body: request as Record<string, unknown>
  })
}

export async function remediateFiles(
  typeId: number,
  fileId: number,
  validation?: RemediateFilesRequest
): Promise<void> {
  // The endpoint is assumed based on legacy: /api/files/:fileId/remediate
  // typeId is included as a query param for backend logic
  return baseApiService.fetch<void>(`/api/periodicreview/${fileId}/remediate`, {
    method: 'POST',
    params: { typeId },
    body: validation ? { ...validation } : {}
  })
}

export async function getFileShareLink(
  fileId: number,
  versionId?: number,
  minorVersionId?: number
): Promise<ShareLinkResponse> {
  const params: Record<string, string | number> = {}
  if (versionId) params.versionId = versionId
  if (minorVersionId) params.minorVersionId = minorVersionId

  return baseApiService.fetch<ShareLinkResponse>(`/api/files/${fileId}/sharelink`, {
    params
  })
}

export interface RenameFileRequest {
  name: string
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function renameFile(fileId: number, request: RenameFileRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/rename`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface MoveFileRequest {
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function moveFile(fileId: number, request: MoveFileRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/move?newParentId=${request.newParentId}`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface MultiMoveFilesRequest {
  ids: number[]
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function moveMultipleFiles(request: MultiMoveFilesRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multiMove', {
    method: 'PATCH',
    body: request
  })
}

export interface MoveAllFilesRequest {
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  filter?: Record<string, unknown>
  [key: string]: unknown
}

export async function moveAllFilesInFolder(parentFolderId: number, request: MoveAllFilesRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${parentFolderId}/moveall`, {
    method: 'PATCH',
    body: request
  })
}

export interface MoveAllFromPropertiesSearchRequest {
  propertySearchRequestDTO: {
    filter: Record<string, unknown>
  }
  operationDTO: {
    newParentId: number
    retainAcls?: boolean
    esignature?: Record<string, unknown>
  }
  [key: string]: unknown
}

export async function moveAllFilesFromPropertiesSearch(request: MoveAllFromPropertiesSearchRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/propertysearch/moveall', {
    method: 'PATCH',
    body: request
  })
}

export interface CopyFileRequest {
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function copyFile(fileId: number, request: CopyFileRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/copy?newParentId=${request.newParentId}`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface MultiCopyFilesRequest {
  ids: number[]
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function copyMultipleFiles(request: MultiCopyFilesRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multiCopy', {
    method: 'PATCH',
    body: request
  })
}

export interface CopyAllFilesRequest {
  newParentId: number
  retainAcls?: boolean
  esignature?: Record<string, unknown>
  filter?: Record<string, unknown>
  [key: string]: unknown
}

export async function copyAllFilesInFolder(parentFolderId: number, request: CopyAllFilesRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${parentFolderId}/copyall`, {
    method: 'PATCH',
    body: request
  })
}

export interface CopyAllFromPropertiesSearchRequest {
  propertySearchRequestDTO: {
    filter: Record<string, unknown>
  }
  operationDTO: {
    newParentId: number
    retainAcls?: boolean
    esignature?: Record<string, unknown>
  }
  [key: string]: unknown
}

export async function copyAllFilesFromPropertiesSearch(request: CopyAllFromPropertiesSearchRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/propertysearch/copyall', {
    method: 'PATCH',
    body: request
  })
}

export interface DeleteFileRequest {
  esignature: Record<string, unknown>
  isPeriodicReview?: boolean
  [key: string]: unknown
}

export async function deleteFile(fileId: number, request: DeleteFileRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/delete`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface UndeleteFileRequest {
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function undeleteFile(fileId: number, request: UndeleteFileRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/undelete`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface PermanentDeleteRequest {
  esignature: Record<string, unknown>
  deletionComment?: string
  [key: string]: unknown
}

export async function permanentDeleteFile(fileId: number, request: PermanentDeleteRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/permanentdelete`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}
export interface PermanentDeleteMultipleRequest {
  ids: number[]
  esignature: Record<string, unknown>
  deletionComment?: string
  [key: string]: unknown
}

export async function permanentDeleteMultipleFiles(request: PermanentDeleteMultipleRequest): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multipermanentdelete', {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface FileUsageResponse {
  docTypeTplsWithTemplateFile?: Array<any>
  [key: string]: unknown
}

export async function getFileUsageById(fileId: number): Promise<FileUsageResponse> {
  return baseApiService.fetch<FileUsageResponse>(`/api/files/${fileId}/usage`)
}

export interface PermanentDeleteAllInFolderRequest {
  esignatureDTO: Record<string, unknown>
  filter: Record<string, unknown>
  deletionComment?: string
  [key: string]: unknown
}

export async function permanentDeleteAllFilesInFolder(
  parentFolderId: number,
  request: PermanentDeleteAllInFolderRequest
): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${parentFolderId}/permanentdeleteall`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface PermanentDeleteAllFromPropertiesSearchRequest {
  propertySearchRequestDTO: {
    filter: Record<string, unknown>
  }
  esignatureDTO: Record<string, unknown>
  deletionComment?: string
  [key: string]: unknown
}

export async function permanentDeleteAllFilesFromPropertiesSearch(
  request: PermanentDeleteAllFromPropertiesSearchRequest
): Promise<void> {
  return baseApiService.fetch<void>('/api/files/propertysearch/permanentdeleteall', {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface RejectFileRequest {
  rejectReasonId?: number | null
  customRejectReasonText?: string
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function rejectFile(fileId: number | number[], rejectionDTO: RejectFileRequest): Promise<void> {
  const requestObj: RejectFileRequest = { ...rejectionDTO }

  if (Array.isArray(fileId)) {
    return baseApiService.fetch<void>('/api/files/multiReject', {
      method: 'PATCH',
      body: { ...requestObj, ids: fileId } as Record<string, unknown>
    })
  } else {
    return baseApiService.fetch<void>(`/api/files/${fileId}/reject`, {
      method: 'PATCH',
      body: requestObj as Record<string, unknown>
    })
  }
}

export interface MultiselectActionFlags {
  canDownloadZip?: boolean
  canReject?: boolean
  canFileToEtmf?: boolean
  canCreateQCReview?: boolean
  canMove?: boolean
  canCopy?: boolean
  canObsolete?: boolean
  canUnobsolete?: boolean
  canPermanentlyDelete?: boolean
  canChangeExpiration?: boolean
  canChangeOwner?: boolean
  canChangeDocType?: boolean
  canShowTags?: boolean
  [key: string]: unknown
}

export interface MultiSelectionsRequestDTO {
  fsiIds: number[]
  [key: string]: unknown
}

/**
 * Get multi-selection action flags for selected FSIs
 * Updated to use POST /api/v2/files/multiselectactions with request body
 * @param parentIdOrFsiIds - Either parentId (legacy) or array of fsiIds
 * @param fsiIds - Array of FSI IDs (optional if first param is array)
 * @returns MultiselectActionFlags indicating available actions
 */
export async function getFsiMultiselectDetailsForActionMenu(
  parentIdOrFsiIds: number | number[],
  fsiIds?: number[]
): Promise<MultiselectActionFlags> {
  // Handle both old signatures for backward compatibility
  let actualFsiIds: number[]

  if (Array.isArray(parentIdOrFsiIds)) {
    // New signature: getFsiMultiselectDetailsForActionMenu([1, 2, 3])
    actualFsiIds = parentIdOrFsiIds
  } else if (fsiIds && fsiIds.length > 0) {
    // Old signature: getFsiMultiselectDetailsForActionMenu(parentId, [1, 2, 3])
    actualFsiIds = fsiIds
  } else {
    throw new Error('fsiIds are required')
  }

  const requestBody: MultiSelectionsRequestDTO = {
    fsiIds: actualFsiIds
  }

  return baseApiService.fetch<MultiselectActionFlags>('/api/v2/files/multiselectactions', {
    method: 'POST',
    body: requestBody as Record<string, unknown>
  })
}

/**
 * @deprecated Use getFsiMultiselectDetailsForActionMenu instead
 * Get multi-selection action flags for selected FSIs (V2 - without parentId)
 */
export async function getFsiMultiselectDetailsForActionMenuV2(fsiIds: number[]): Promise<MultiselectActionFlags> {
  return getFsiMultiselectDetailsForActionMenu(fsiIds)
}

export interface SetOwnerRequest {
  ownerId: number
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function setFileOwner(fileId: number, request: SetOwnerRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/owner`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export interface SetFilesOwnerRequest {
  fileIds: number[]
  ownerId: number
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function setFilesOwner(
  fileIds: number[],
  ownerId: number,
  validation?: Record<string, unknown>,
  options?: Record<string, unknown>
): Promise<void> {
  return baseApiService.fetch<void>('/api/files/multiSetOwner', {
    method: 'PATCH',
    body: {
      ids: fileIds,
      ownerId,
      esignature: validation,
      ...options
    } as Record<string, unknown>
  })
}

export interface SetExpirationRequest {
  expiresAt: string | null
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function setExpirationDateFile(fileId: number, request: SetExpirationRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/expiration`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

/**
 * Set expiration date for multiple files
 * Supports three operation modes:
 * 1. Standard multi-file (default)
 * 2. Property search batch (selectedWholeResult + isPropertiesSearch)
 * 3. Folder-wide batch (selectedWholeResult + parentFolderId)
 *
 * @param filesIds - Array of file IDs to update
 * @param expiresAt - Unix timestamp or null to clear expiration
 * @param validation - E-signature validation data
 * @param options - Batch operation options
 */
export async function setExpirationDateFiles(
  filesIds: number[],
  expiresAt: number | null,
  validation?: Record<string, unknown>,
  options?: {
    selectedWholeResult?: boolean
    isPropertiesSearch?: boolean
    parentFolderId?: number
    filter?: any
    meta?: any
  }
): Promise<void> {
  const requestObj: Record<string, unknown> = {
    ids: filesIds,
    expiresAt: expiresAt,
    esignature: validation
  }

  // Property search batch operation
  if (options?.selectedWholeResult && options.isPropertiesSearch && options.filter?.metadataCriteria) {
    const filter = { ...options.filter, workflowIsNull: true }
    return baseApiService.fetch<void>('/api/files/propertysearch/setexpirationdateforall', {
      method: 'PATCH',
      body: {
        propertySearchRequestDTO: { filter },
        operationDTO: requestObj
      }
    })
  }

  // Folder-wide batch operation
  if (options?.selectedWholeResult && options.parentFolderId) {
    const requestWithFilter = { ...requestObj, filter: options.filter }
    return baseApiService.fetch<void>(`/api/files/${options.parentFolderId}/setexpirationdateforall`, {
      method: 'PATCH',
      body: requestWithFilter
    })
  }

  // Standard multi-file operation
  return baseApiService.fetch<void>('/api/files/multiSetExpirationDate', {
    method: 'PATCH',
    body: requestObj
  })
}

/**
 * Export folder contents as ZIP with custom filters
 * Matches legacy: api.files.exportZipCustomReports(filterData, validation)
 *
 * @param filterData - Complex filter object with export options
 * @param esignature - E-signature validation object
 * @returns Promise that resolves when export is initiated (async process)
 *
 * Filter structure:
 * - parentId: Folder ID to export from
 * - name: Filename filter (optional)
 * - nameMatchType: 'EQUALS' | 'CONTAINS' (optional)
 * - mimetype: Array of mimetype categories (optional)
 * - lifecycleState: 'EFFECTIVE' | 'OBSOLETE' | 'REJECTED' (optional)
 * - fsiTypeTemplate: Array of document type IDs (optional)
 * - size: File size in bytes (optional)
 * - sizeType: 'LESSER' | 'GREATER' (optional)
 * - createdAt: Unix timestamp (optional)
 * - createdSpec: 'ON' | 'BEFORE' | 'AFTER' (optional)
 * - modifiedAt: Unix timestamp (optional)
 * - modifiedSpec: 'ON' | 'BEFORE' | 'AFTER' (optional)
 * - exportType: 'SOURCE' | 'PDF'
 * - includeSubFolders: boolean
 * - includeEmptyFolders: boolean
 * - generatePropertyFile: boolean
 * - includeMajorHistoricalVersions: boolean
 */
export interface ExportZipCustomReportsRequest {
  parentId: number
  name?: string
  nameMatchType?: 'EQUALS' | 'CONTAINS'
  mimetype?: string[]
  lifecycleState?: string
  fsiTypeTemplate?: number[]
  size?: number
  sizeType?: 'LESSER' | 'GREATER'
  createdAt?: number
  createdSpec?: 'ON' | 'BEFORE' | 'AFTER'
  modifiedAt?: number
  modifiedSpec?: 'ON' | 'BEFORE' | 'AFTER'
  exportType: 'SOURCE' | 'PDF'
  includeSubFolders: boolean
  includeEmptyFolders: boolean
  generatePropertyFile: boolean
  includeMajorHistoricalVersions: boolean
  esignature?: Record<string, unknown>
}

export async function exportZipCustomReports(
  filterData: ExportZipCustomReportsRequest,
  esignature?: Record<string, unknown>
): Promise<void> {
  const requestBody = {
    filter: filterData,
    esignature: esignature || filterData.esignature
  }

  await baseApiService.fetch<void>('/api/reports/report/zipcustomreportdocuments', {
    method: 'POST',
    body: requestBody
  })
}

export interface ChangeDocTypeRequest {
  docTypeId: number
  esignature?: Record<string, unknown>
  [key: string]: unknown
}

export async function changeFileDocType(fileId: number, request: ChangeDocTypeRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/doctype`, {
    method: 'PATCH',
    body: request as Record<string, unknown>
  })
}

export async function markFileAsViewed(
  fileId: number,
  versionId?: number,
  minorVersionId?: number,
  taskId?: number | null
): Promise<void> {
  const params: Record<string, string | number> = {}
  if (versionId) params.versionId = versionId
  if (minorVersionId) params.minorVersionId = minorVersionId
  if (taskId) params.taskId = taskId

  return baseApiService.fetch<void>(`/api/files/${fileId}/viewed`, {
    method: 'POST',
    params
  })
}

export async function getFileViewed(
  fileId: number,
  versionId?: number | null,
  minorVersionId?: number | null,
  taskId?: number | null
): Promise<{ viewed: boolean }> {
  const params: Record<string, string | number> = {}
  if (versionId) params.versionId = versionId
  if (minorVersionId) params.minorVersionId = minorVersionId
  if (taskId) params.taskId = taskId

  return baseApiService.fetch<{ viewed: boolean }>(`/api/files/${fileId}/viewed`, {
    params
  })
}

export async function getPdfUrlFromUuid(uuid: string): Promise<{ url: string }> {
  return baseApiService.fetch<{ url: string }>(`/api/files/pdf/${uuid}`)
}

export async function getPdfDiffUrlFromUuid(uuid: string): Promise<{ url: string }> {
  return baseApiService.fetch<{ url: string }>(`/api/files/pdfdiff/${uuid}`)
}

export async function getFilePdfDownloadUrl(
  fileId: number,
  forDownload = false,
  forPrint = false,
  params?: any
): Promise<{ url: string; s3Url?: string; message?: string; uuidToCheckState: string }> {
  const queryParams = {
    forDownload,
    forPrint,
    ...params
  }

  return baseApiService.fetch(`/api/files/${fileId}/convert/watermarkedpdf`, {
    params: queryParams as Record<string, string | number | boolean | string[] | number[]>
  })
}

export async function getFileVersionPdfDownloadUrl(
  fileId: number,
  versionId: number,
  forDownload = false,
  minorVersionId?: number,
  forPrint = false,
  params?: any
): Promise<{ url: string; s3Url?: string; message?: string; uuidToCheckState: string }> {
  const queryParams: any = {
    versionId,
    forDownload,
    forPrint,
    ...params
  }

  if (minorVersionId) {
    queryParams.minorVersionId = minorVersionId
  }

  return baseApiService.fetch(`/api/files/${fileId}/convert/watermarkedpdf`, {
    params: queryParams as Record<string, string | number | boolean | string[] | number[]>
  })
}

export interface ConversionStatusResponse {
  conversionState: string
  url?: string
  s3Url?: string
  uuidToCheckState?: string
  message?: string
}

export async function checkConversionStatus(uuid: string): Promise<ConversionStatusResponse> {
  return baseApiService.fetch<ConversionStatusResponse>(`/api/files/conversionStatus/${uuid}`)
}

// --- Conversion polling logic (migrated from legacy file.service.js) ---

const CONVERSION_CHECK_INTERVAL = 1000
const CONVERSION_STATE = {
  CONVERTED_OK: 'CONVERTED_OK',
  CONVERTING: 'CONVERTING',
  FAILED: 'FAILED'
} as const

interface ConversionCheck {
  service: () => Promise<ConversionStatusResponse>
  handler?: Promise<void>
  request?: Promise<ConversionStatusResponse>
  finished?: boolean
}

const downloadCheckMap: Record<number, ConversionCheck> = {}

function checkBeforePageReload(event: BeforeUnloadEvent) {
  const isConverting = Object.values(downloadCheckMap).some((entry) => !entry.finished)
  if (isConverting) {
    event.preventDefault()
    event.returnValue = ''
  }
}

function downloadUri(uri: string) {
  if (uri) {
    window.removeEventListener('beforeunload', checkBeforePageReload)
    const link = document.createElement('a')
    link.href = uri
    link.download = 'document'
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => {
      window.addEventListener('beforeunload', checkBeforePageReload)
    }, 200)
  }
}

function onConversionFailure(fileId: number, data?: any) {
  downloadCheckMap[fileId].finished = true
  const message = data?.data?.message ?? data?.message

  toast.error(message ?? 'ERROR')

  // Dispatch a custom event so UI layers (toast, etc.) can react
  window.dispatchEvent(
    new CustomEvent('conversion-failed', {
      detail: {
        fileId,
        message: message || 'ERROR_IN_GENERATING_PROCESS'
      }
    })
  )
}

function pollConversion(fileId: number) {
  setTimeout(async () => {
    const entry = downloadCheckMap[fileId]
    if (!entry) return

    entry.request = entry.service()
    const handler = entry.request
      .then((data) => {
        // Only handle if this is still the latest handler (prevents stale responses)
        if (handler === downloadCheckMap[fileId]?.handler) {
          if (data) {
            switch (data.conversionState) {
              case CONVERSION_STATE.CONVERTED_OK:
                downloadCheckMap[fileId].finished = true
                downloadUri(postprocessLocationInfo(data).url!)
                break
              case CONVERSION_STATE.CONVERTING:
                pollConversion(fileId)
                break
              case CONVERSION_STATE.FAILED:
                onConversionFailure(fileId, data)
                break
              default:
                onConversionFailure(fileId, data)
                break
            }
          } else {
            onConversionFailure(fileId)
          }
        }
      })
      .catch((err) => {
        if (handler === downloadCheckMap[fileId]?.handler) {
          onConversionFailure(fileId, err)
        }
      }) as unknown as Promise<void>

    entry.handler = handler
  }, CONVERSION_CHECK_INTERVAL)
}

/**
 * Start polling for PDF conversion status and automatically download when ready.
 * Migrated from legacy file.service.js `startCheckingConversion`.
 * @param uuid - The conversion UUID returned by the server
 * @param fileId - The file ID being converted
 */
export function startCheckingConversion(uuid: string, fileId: number): void {
  downloadCheckMap[fileId] = {
    service: () => checkConversionStatus(uuid)
  }
  pollConversion(fileId)
  window.addEventListener('beforeunload', checkBeforePageReload)
}

/**
 * Download a specific version of a file as PDF.
 * Migrated from legacy file.service.js `downloadFsiVersionPdf`.
 * Validates the file, fetches the version PDF download URL, then either
 * downloads directly, starts conversion polling, or shows an error.
 * @param fsiId - File system item ID
 * @param versionId - Major version ID
 * @param minorVersionId - Minor version ID
 */
export async function downloadFsiVersionPdf(fsiId: number, versionId: number, minorVersionId?: number): Promise<void> {
  try {
    const validation = await validateFile(fsiId)
    if (!validation.value) {
      return
    }

    try {
      const resp = await getFileVersionPdfDownloadUrl(fsiId, versionId, true, minorVersionId)
      if (resp && resp.message) {
        window.dispatchEvent(new CustomEvent('toast-info', { detail: { message: resp.message } }))
      }

      const fileInfo = postprocessLocationInfo(resp)

      if (typeof fileInfo.url === 'string') {
        downloadUri(fileInfo.url)
      } else if (fileInfo.uuidToCheckState) {
        startCheckingConversion(fileInfo.uuidToCheckState, fsiId)
      } else {
        window.dispatchEvent(
          new CustomEvent('toast-error', {
            detail: { messageKey: 'GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER' }
          })
        )
      }
    } catch (error: any) {
      if (error?.code === 421 || error?.status === 421) {
        window.dispatchEvent(
          new CustomEvent('toast-info', {
            detail: { messageKey: 'GENERATING_VIEWABLE_DOC_RETRY_ACTION_LATER' }
          })
        )
      } else {
        window.dispatchEvent(
          new CustomEvent('toast-error', {
            detail: { message: error?.body?.message || error?.message || 'Download failed' }
          })
        )
      }
    }
  } catch (error: any) {
    console.error('[downloadFsiVersionPdf] Validation error:', error)
  }
}

export async function getZipDownloadUrlByUuid(uuid: string): Promise<{ url: string }> {
  return baseApiService.fetch<{ url: string }>(`/api/files/zip/${uuid}`)
}

// Folder related functions
export interface CreateFolderRequest {
  name: string
  parentId: number
  folderTypeId?: number
  [key: string]: unknown
}

export async function createFolder(request: CreateFolderRequest): Promise<{ id: number }> {
  return baseApiService.fetch<{ id: number }>('/api/files/createfolder', {
    method: 'POST',
    body: request as Record<string, unknown>
  })
}

export async function getFolders(
  parentId: number,
  filter?: Record<string, string | number | boolean | string[] | number[]>
): Promise<any> {
  return baseApiService.fetch(`/api/folders/${parentId}`, {
    params: filter
  })
}

export async function getFolder(
  folderId: number,
  filter?: Record<string, string | number | boolean | string[] | number[]>
): Promise<any> {
  return baseApiService.fetch(`/api/folders/${folderId}/details`, {
    params: filter
  })
}

// Search functions
export async function search(params: {
  fields: string | string[]
  phrase: string | number
  limit: number
  offset: number
  status?: string | null
  itemType?: string | null
  type?: number
  updatedType?: string | null
  updatedFrom?: number
  updatedTo?: number
}): Promise<any> {
  const queryParams: Record<string, any> = {
    phrase: params.phrase,
    limit: params.limit,
    offset: params.offset
  }

  // Handle fields parameter (can be array or string)
  if (Array.isArray(params.fields)) {
    // For multiple fields, pass as array and baseApiService will handle repeated params
    queryParams.fields = params.fields
  } else {
    queryParams.fields = params.fields
  }

  // Add optional filters (only if not null/undefined)
  if (params.status) queryParams.status = params.status
  if (params.itemType) queryParams.itemType = params.itemType
  if (params.type) queryParams.type = params.type
  if (params.updatedType) queryParams.updatedType = params.updatedType
  if (params.updatedFrom) queryParams.updatedFrom = params.updatedFrom
  if (params.updatedTo) queryParams.updatedTo = params.updatedTo

  return baseApiService.fetch('/api/files/search', {
    params: queryParams
  })
}

/**
 * Property search for files (entity/property search)
 * @param filter - Search filter object
 * @param meta - Optional meta object
 * @param hideLoader - Optional flag to hide loader (not used in new API, for parity)
 * @returns Promise<any>
 */
export async function propertySearch(
  filter: Record<string, unknown>,
  meta?: Record<string, unknown>,
  hideLoader?: boolean
): Promise<any> {
  // Match the legacy API endpoint: POST to /api/metadatasearch with { filter, meta, tag }
  const reqObj = {
    filter,
    meta,
    tag: filter.tag === null ? null : filter.tag
  }
  return baseApiService.fetch('/api/metadatasearch', {
    method: 'POST',
    body: reqObj
  })
}

export async function propertySearchExport(
  filter: Record<string, unknown>,
  meta?: Record<string, unknown>
): Promise<any> {
  return baseApiService.fetch('/api/files/propertysearch/export', {
    method: 'POST',
    body: { filter, meta }
  })
}

/**
 * Get count of files matching property search filter
 * @param filter - Property search filter object
 * @returns Promise with count (number) or anyFilterActive flag
 */
export async function propertySearchGetCount(
  filter: Record<string, unknown>
): Promise<number | { anyFilterActive: false }> {
  // Check if any filter is active (mimics isPropSearchFilterActive from legacy)
  const hasActiveFilter = Object.keys(filter).some((key) => {
    const value = filter[key]
    return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)
  })

  if (!hasActiveFilter) {
    return { anyFilterActive: false }
  }

  const reqObj = {
    filter: {
      ...filter,
      workflowIsNull: true
    }
  }

  // The API returns a plain number
  return baseApiService.fetch<number>('/api/metadatasearch/countall', {
    method: 'POST',
    body: reqObj
  })
}

export async function searchSuggestion(phrase: string): Promise<any> {
  return baseApiService.fetch('/api/files/search/suggestions', {
    params: { phrase }
  })
}

// Additional utility functions
export async function addFileToFavorite(fileId: number, filename: string): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/favorite`, {
    method: 'POST',
    body: { filename }
  })
}

export async function removeFileFromFavorite(fileId: number): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/favorite?favorite=false`, {
    method: 'PATCH'
  })
}

export async function renameFavorite(fileId: number, newName: string): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/favorite/rename`, {
    method: 'PATCH',
    body: { name: newName }
  })
}

export async function sendShareLinkEmail(userIds: number[], subject: string, uuid: string): Promise<void> {
  return baseApiService.fetch<void>('/api/files/sharelink/email', {
    method: 'POST',
    body: { userIds, subject, uuid }
  })
}

export async function logPrint(fileId: number): Promise<void> {
  return baseApiService.fetch<void>(`/api/v2/files/${fileId}/printed`, {
    method: 'POST'
  })
}

export async function logDocViewedInBrowserTab(fileId: number): Promise<void> {
  return baseApiService.fetch<void>(`/api/v1/files/documentviewedinbrowser/${fileId}`, {
    method: 'PATCH'
  })
}

export async function getOCRVersionFileUrl(fileId: string, versionId: string): Promise<{ url: string }> {
  return baseApiService.fetch<{ url: string }>(`/api/v2/files/${fileId}/extracted_pdf?vid=${versionId}`)
}

// Tag Options API
export interface TagOption {
  id: number
  name: string
  [key: string]: any
}

/**
 * Get tag options for filtering/selection
 * @param filter - Optional filter parameters
 * @param sortAlphabetical - Whether to sort results alphabetically by name
 * @param isSilent - Whether to suppress loading indicator (not used in new API)
 * @returns Array of tag options
 */
export async function getTagOptions(
  filter?: Record<string, unknown>,
  sortAlphabetical?: boolean,
  isSilent?: boolean
): Promise<TagOption[]> {
  const response = await baseApiService.fetch<any>('/api/tags/options/', {
    params: (filter || {}) as QueryParams
  })

  // Handle both array and { elements: [] } response structures
  const tags = response?.elements || response || []

  if (sortAlphabetical && Array.isArray(tags)) {
    return tags.sort((a, b) => a.name.localeCompare(b.name))
  }

  return tags
}

// Periodic Review functions
export async function getPeriodicFiles(
  filter?: Record<string, string | number | boolean | string[] | number[]>
): Promise<FilesResponse> {
  return baseApiService.fetch<FilesResponse>('/api/periodicreview/files', {
    params: filter
  })
}

export function getThumbnailUrl(fsi: { thumbnailId?: string }, size?: number): string {
  if (!fsi || !fsi.thumbnailId) {
    throw new Error('Missing thumbnail ID')
  }

  const defSize = 256
  const thumbnailSize = size ?? defSize
  const tenantUuid = getTenantUuid()?.split(' ')[0] || ''

  // Get thumbnail-url from HTML attribute (set by backend)

  // In Angular version, this comes from angular.element('html').attr('thumbnail-url')
  const thumbnailBaseUrl = document.documentElement.getAttribute('thumbnail-url') || '/api/files/thumbnail/'

  return `${thumbnailBaseUrl}${fsi.thumbnailId}?size=${thumbnailSize}&tenantUuid=${tenantUuid}`
}

// Custom Reports API methods
export async function getMimeTypeCategories(includeAll?: boolean): Promise<string[]> {
  const params = includeAll ? { includeAll: true } : {}
  const response = await baseApiService.fetch<string[]>('/api/reports/mimetypecategories', { params })
  return response
}

export async function exportCustomReports(filter: any): Promise<void> {
  await baseApiService.fetch('/api/reports/exportcustomReport', {
    method: 'POST',
    body: JSON.stringify(filter)
  })
}

export async function getDocumentExpiredReport(filter: any, meta: any): Promise<any> {
  const reqObj = { meta: meta, filter: filter }
  const response = await baseApiService.fetch('/api/reports/documentexpiredreport', {
    method: 'POST',
    body: JSON.stringify(reqObj)
  })
  return response
}

export async function exportDocumentExpiredReport(filter: any, meta: any): Promise<void> {
  const reqObj = { meta: meta, filter: filter }
  await baseApiService.fetch('/api/reports/documentexpiredexport', {
    method: 'POST',
    body: JSON.stringify(reqObj)
  })
}
/**
 * Get the next document in a folder (for "File & Go Next" feature)
 * @param fileId - Current file ID
 * @param filter - Filter parameters (sortBy, sortDesc, etc.)
 */
export async function getNextDocument(
  fileId: number,
  filter?: Record<string, string | number | boolean>
): Promise<{ id: number; name: string } | null> {
  return baseApiService.fetch<{ id: number; name: string } | null>(`/api/v2/files/${fileId}/nextfile`, {
    params: filter
  })
}

/**
 * File References
 */
export interface FileReference {
  referenceFsiId: number
  referenceType: 'DOCUMENT' | 'FORM' | 'AMENDMENT'
  filename: string
}

export interface ESignature {
  username?: string
  password?: string
  reason?: string
}

/**
 * Update file/folder details with optional e-signature
 */
export async function changeFSIDetails(
  fileId: number,
  data: Partial<FileDetail>,
  validation?: ESignature
): Promise<FileDetail> {
  return baseApiService.fetch<FileDetail>(`/api/v1/files/${fileId}/changeFSIDetails`, {
    method: 'PATCH',
    body: {
      ...data,
      esignature: validation
    }
  })
}

/**
 * Get file references (related documents)
 */
export async function getFileReferences(fileId: number): Promise<FileReference[]> {
  return baseApiService.fetch<FileReference[]>(`/api/files/${fileId}/documentrefs`)
}

/**
 * Set file references (update related documents)
 */
export async function setFileReferences(
  fileId: number,
  references: FileReference[],
  validation?: ESignature
): Promise<void> {
  return baseApiService.fetch<void>(`/api/files/${fileId}/documentrefs`, {
    method: 'PATCH',
    body: {
      references,
      esignature: validation
    }
  })
}

/**
 * ============================================================================
 * PROPERTIES & METADATA APIs
 * ============================================================================
 */

export interface MetadataElement {
  id: number | null
  metadataTemplateId: number
  value: any
  inherited: boolean
  inheritedValue?: any
  revertToInherited?: boolean
  isChanged?: boolean
  mixedValue?: boolean
  mixedValueRequiredEmpty?: boolean
  requiredOnFSITemplate?: boolean
  checkRequired?: boolean
  applyRecursively?: boolean
  template?: MetadataTemplate
  itemCopy?: MetadataElement
  isPlatformProperty?: boolean
}

export interface MetadataTemplate {
  id: number
  name: string
  type: string
  definition?: {
    options?: Array<{ id: string | number; name: string }>
    reactOptions?: Array<{ label: string; value: string | number }>
    [key: string]: any
  }
  [key: string]: any
}

export interface FilePropertiesDetail extends FileDetail {
  metadata: {
    elements: MetadataElement[]
  }
  canEditProperties: boolean
  templateName?: string
  templateId?: number
}

export interface FilePropertiesOptions {
  parentFolderId?: number
  selectedWholeResult?: boolean
}

/**
 * Get file/folder details with properties (metadata)
 * @param fileId - File or folder ID
 */
export async function getDetailsForProperties(fileId: number): Promise<FilePropertiesDetail> {
  return baseApiService.fetch<FilePropertiesDetail>(`/api/files/${fileId}/properties`)
}

/**
 * Get intersected properties for multiple files
 * Supports two modes:
 * 1. Specific file IDs: Returns common properties across selected files
 * 2. Whole result set: Returns common properties for all files in a folder
 *
 * @param fileIds - Array of file IDs
 * @param options - Options for whole result set mode
 */
export async function getDetailsForPropertiesMultipleFiles(
  fileIds: number[],
  options?: FilePropertiesOptions
): Promise<FilePropertiesDetail> {
  if (options?.selectedWholeResult && options?.parentFolderId) {
    // Get properties for all files in folder
    return baseApiService.fetch<FilePropertiesDetail>(`/api/files/${options.parentFolderId}/propertiesintersectedall`)
  } else {
    // Get intersected properties for specific files
    const fileIdsStr = Array.isArray(fileIds) ? fileIds.join(',') : String(fileIds)
    return baseApiService.fetch<FilePropertiesDetail>(`/api/files/${fileIdsStr}/propertiesIntersected`)
  }
}

/**
 * Modify file metadata (properties)
 * Supports updating single file, multiple files, or all files in folder
 *
 * @param fileId - Single file ID or array of file IDs
 * @param metadata - Array of metadata elements to update
 * @param options - Options for whole result set mode
 */
export async function modifyFileMetadatas(
  fileId: number | number[],
  metadata: MetadataElement[],
  options?: FilePropertiesOptions
): Promise<void> {
  const reqObj = { metadata }

  if (options?.selectedWholeResult && options?.parentFolderId) {
    // Update metadata for all files in folder
    return baseApiService.fetch<void>(`/api/files/${options.parentFolderId}/modifymetadatasall`, {
      method: 'PUT',
      body: reqObj
    })
  } else {
    // Update metadata for specific file(s)
    const fileIdsStr = Array.isArray(fileId) ? fileId.join(',') : String(fileId)
    return baseApiService.fetch<void>(`/api/files/${fileIdsStr}/metadatas`, {
      method: 'PUT',
      body: reqObj
    })
  }
}

/**
 * Evaluate inherited metadata value from parent folder
 * Used when reverting a property to its inherited value
 *
 * @param fileId - File or folder ID
 * @param metadataId - Metadata element ID
 * @returns The inherited value from parent
 */
export async function evalInherited(fileId: number, metadataId: number): Promise<{ value: any }> {
  return baseApiService.fetch<{ value: any }>(`/api/files/${fileId}/metadata/${metadataId}/evalinherited`)
}

/**
 * Update a single direct metadata value
 * @param fileId - File ID
 * @param metadataId - Metadata element ID
 * @param value - New value
 */
export async function updateFileDirectMetadata(fileId: number, metadataId: number, value: any): Promise<void> {
  return baseApiService.fetch<void>(`/api/v2/files/${fileId}/metadata/${metadataId}`, {
    method: 'PUT',
    body: { value }
  })
}

/**
 * Create a new direct metadata entry
 * @param fileId - File ID
 * @param metadataTemplateId - Template ID
 * @param value - Value
 */
export async function createFileDirectMetadata(fileId: number, metadataTemplateId: number, value: any): Promise<void> {
  return baseApiService.fetch<void>(`/api/v2/files/${fileId}/metadata`, {
    method: 'POST',
    body: {
      metadataTemplateId,
      value
    }
  })
}

// ============================================================================
// Workflow & Change Request Support Methods
// Added for AddDocumentModal migration
// ============================================================================

export interface FolderTypeResponse {
  id: number
  name: string
  documentTypesAllowed: string
  allowedDocumentTypeTemplateIds: number[]
  allowCertifiedCopies: boolean
}

export interface WorkflowApproversResponse {
  initialApprovers: {
    type: string
    steps?: any[]
  }
}

export interface WorkflowCollaboratorsResponse {
  collaborators: {
    type: string
    steps?: any[]
  }
}

export interface WorkflowReviewersResponse {
  reviewers: {
    type: string
    steps?: any[]
  }
}

export interface WorkflowApproversFullResponse {
  approvers: {
    type: string
    steps?: any[]
  }
}

/**
 * Get folder type configuration including allowed document types
 * Used when adding documents to determine document type restrictions
 *
 * @param folderId - Parent folder ID
 * @returns Folder type configuration
 */
export async function getFolderFolderType(folderId: number): Promise<FolderTypeResponse> {
  return baseApiService.fetch<FolderTypeResponse>(`/api/folders/${folderId}/foldertypetemplate`)
}

/**
 * Get file approvers (V2 - raw API response)
 * Returns approvers data directly from the API without processing
 *
 * @param fileId - File ID
 * @returns Raw approvers configuration from API
 */
export async function getFileApproversV2(fileId: number): Promise<FileApproversResponse> {
  return baseApiService.fetch<FileApproversResponse>(`/api/files/${fileId}/approvers`)
}

/**
 * Get file approvers with assembled user/group data
 * This is the legacy V1 implementation that merges approvers with available users/groups
 *
 * @param fileId - File ID
 * @returns Approvers configuration with merged user/group data
 * Export property search results to report
 * Migrated from AngularJS propertySearchExport
 * @param filter - property search filter object
 * @param meta - optional metadata object
 * @returns Promise<any> (API returns file download or export status)
 */

export async function getFileApprovers(fileId: number): Promise<FileApproversProcessed> {
  // First get assembled users and groups
  const assembledData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = assembledData.merged

  // Then get file approvers
  const data = await baseApiService.fetch<FileApproversResponse>(`/api/files/${fileId}/approvers`)

  const result: FileApproversProcessed = {
    approvers: {
      type: data.approvers.type,
      steps: [],
      approveActions: []
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process approver steps
  for (const step of data.approvers.steps) {
    const approverObj: WorkflowStep = {
      processingType: step.processingType,
      taskTeamStepLabel: step.taskTeamStepLabel,
      nofAllValue: step.nofAllValue,
      taskTeams: step.taskTeams,
      merged: []
    }

    // Merge task teams with available users/groups
    for (const taskTeam of step.taskTeams) {
      const id = taskTeam.group ? taskTeam.group.id : taskTeam.user?.id
      const systemHelperType = taskTeam.group ? 'GROUP' : 'USER'

      const found = availableUsersGroups.find(
        (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
      )

      if (found) {
        approverObj.merged!.push(found as any)
      }
    }

    result.approvers.steps.push(approverObj)
  }

  // Process approve actions if present
  if (data.approvers.approveActions) {
    for (const action of data.approvers.approveActions) {
      const dynActionObj: ApproveActionProcessed = {
        actionType: action['@type'],
        actionData: action.actionData,
        notifies: action.notifies,
        merged: []
      }

      // Merge notifies with available users/groups
      for (const notify of action.notifies) {
        const id = notify.group ? notify.group.id : notify.user?.id
        const systemHelperType = notify.group ? 'GROUP' : 'USER'

        const found = availableUsersGroups.find(
          (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
        )

        if (found) {
          dynActionObj.merged.push(found as any)
        }
      }

      // Handle action-specific fields
      if (dynActionObj.actionType === 'MOVE') {
        dynActionObj.selectedFolder = action.moveToFsiId
      } else if (dynActionObj.actionType === 'COPY') {
        dynActionObj.selectedFolder = action.copyToFsiId
      } else if (dynActionObj.actionType === 'NOTIFY') {
        dynActionObj.actionData = action.customNotificationText
      }

      result.approvers.approveActions.push(dynActionObj)
    }
  }

  return result
}

/**
 * Get file reviewers with assembled user/group data
 *
 * @param fileId - File ID
 * @returns Reviewers configuration with merged user/group data
 */
export async function getFileReviewers(fileId: number): Promise<FileReviewersProcessed> {
  // First get assembled users and groups
  const assembledData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = assembledData.merged

  // Then get file reviewers
  const data = await baseApiService.fetch<FileReviewersResponse>(`/api/files/${fileId}/reviewers`)

  const result: FileReviewersProcessed = {
    reviewers: {
      type: data.reviewers.type,
      steps: []
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process reviewer steps
  for (const step of data.reviewers.steps) {
    const reviewerObj: WorkflowStep = {
      processingType: step.processingType,
      taskTeamStepLabel: step.taskTeamStepLabel,
      nofAllValue: step.nofAllValue,
      taskTeams: step.taskTeams,
      merged: []
    }

    // Merge task teams with available users/groups
    for (const taskTeam of step.taskTeams) {
      const id = taskTeam.group ? taskTeam.group.id : taskTeam.user?.id
      const systemHelperType = taskTeam.group ? 'GROUP' : 'USER'

      const found = availableUsersGroups.find(
        (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
      )

      if (found) {
        reviewerObj.merged!.push(found as any)
      }
    }

    result.reviewers.steps.push(reviewerObj)
  }

  return result
}

/**
 * Get file editors/collaborators with assembled user/group data
 *
 * Similar to getFileApprovers() but for collaborators.
 * Optionally adds the current user if addCurrentUser is true and type is SETUP_WITH_WORKFLOW.
 *
 * @param fsiId - File/folder ID
 * @param addCurrentUser - Whether to automatically add current user as collaborator
 * @returns Collaborators configuration with merged user/group data
 */
export async function getFsiEditors(fsiId: number, addCurrentUser = false): Promise<FileEditorsProcessed> {
  // First get assembled users and groups
  const assembledData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = assembledData.merged

  // Then get file collaborators
  const data = await baseApiService.fetch<FileEditorsResponse>(`/api/files/${fsiId}/collaborators`)

  const result: FileEditorsProcessed = {
    collaborators: {
      type: data.collaborators.type,
      steps: []
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process collaborator steps
  for (const step of data.collaborators.steps) {
    const collaboratorObj: WorkflowStep = {
      processingType: step.processingType,
      taskTeamStepLabel: step.taskTeamStepLabel,
      nofAllValue: step.nofAllValue,
      taskTeams: step.taskTeams,
      merged: []
    }

    // Merge task teams with available users/groups
    for (const taskTeam of step.taskTeams) {
      const id = taskTeam.group ? taskTeam.group.id : taskTeam.user?.id
      const systemHelperType = taskTeam.group ? 'GROUP' : 'USER'

      const found = availableUsersGroups.find(
        (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
      )

      if (found) {
        collaboratorObj.merged!.push(found as any)
      }
    }

    result.collaborators.steps.push(collaboratorObj)
  }

  // Optionally add current user
  if (addCurrentUser && result.collaborators.type === 'SETUP_WITH_WORKFLOW') {
    const currentUser = getUser()
    if (currentUser) {
      const currentUserInList = availableUsersGroups.find(
        (user: User | Group) => user.id === currentUser.id && (user as any).systemHelperType === 'USER'
      )

      if (currentUserInList) {
        result.collaborators.steps.push({
          processingType: 'SERIAL',
          nofAllValue: '',
          taskTeamStepLabel: '',
          taskTeams: [],
          merged: [currentUserInList as any]
        })
        console.log('Collaborator was added automatically')
      }
    }
  }

  return result
}

/**
 * Get change request initial approvers with assembled user/group data
 *
 * Similar to getFileApprovers() but for change request workflows.
 * Optionally adds the current user if addCurrentUser is true and type is SETUP_WITH_WORKFLOW.
 *
 * @param fileId - File ID
 * @param addCurrentUser - Whether to automatically add current user as approver
 * @returns Initial approvers configuration with merged user/group data
 */
export async function getFileCRApprovers(fileId: number, addCurrentUser = false): Promise<FileCRApproversProcessed> {
  // First get assembled users and groups
  const assembledData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = assembledData.merged

  // Then get file initial approvers (showLoader: false in original)
  const data = await baseApiService.fetch<FileCRApproversResponse>(`/api/files/${fileId}/initialapprovers`)

  const result: FileCRApproversProcessed = {
    initialApprovers: {
      type: data.initialApprovers.type,
      steps: []
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process initial approver steps
  for (const step of data.initialApprovers.steps) {
    const approverObj: WorkflowStep = {
      processingType: step.processingType,
      taskTeamStepLabel: step.taskTeamStepLabel,
      nofAllValue: step.nofAllValue,
      taskTeams: step.taskTeams,
      merged: []
    }

    // Merge task teams with available users/groups
    for (const taskTeam of step.taskTeams) {
      const id = taskTeam.group ? taskTeam.group.id : taskTeam.user?.id
      const systemHelperType = taskTeam.group ? 'GROUP' : 'USER'

      const found = availableUsersGroups.find(
        (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
      )

      if (found) {
        approverObj.merged!.push(found as any)
      }
    }

    result.initialApprovers.steps.push(approverObj)
  }

  // Optionally add current user
  if (addCurrentUser && result.initialApprovers.type === 'SETUP_WITH_WORKFLOW') {
    const currentUser = getUser()
    if (currentUser) {
      const currentUserInList = availableUsersGroups.find(
        (user: User | Group) => user.id === currentUser.id && (user as any).systemHelperType === 'USER'
      )

      if (currentUserInList) {
        result.initialApprovers.steps.push({
          processingType: 'SERIAL',
          nofAllValue: '',
          taskTeamStepLabel: '',
          taskTeams: [],
          merged: [currentUserInList as any]
        })
        console.log('Authorizer was added automatically')
      }
    }
  }

  return result
}

/**
 * Get files with matching checksum
 *
 * @param checksum - SHA1 checksum string to search for
 * @returns Response containing files with matching checksum
 */
export async function getCalculatedChecksum(checksum: string): Promise<any> {
  const requestObj = {
    files: checksum
  }
  return baseApiService.fetch<any>('/api/files/calculatedchecksum', {
    params: requestObj
  })
}

/**
 * Get file size limit for checksum calculation
 *
 * @returns File size limit configuration
 */
export async function getFileSizeLimitToCalculateChecksum(): Promise<any> {
  return baseApiService.fetch<any>('/api/files/filesizelimit')
}

// ============================================================
// Workflow Participant Assembly for Server Submission
// ============================================================

/**
 * Interface for UI representation of workflow step with merged user/group data
 */
interface WorkflowStepUI {
  processingType: string
  nofAllValue?: number | null
  taskTeamStepLabel?: {
    id?: number
    value?: number
    label?: string
    text?: string
  } | null
  merged: Array<{
    id: number
    systemHelperType: 'USER' | 'GROUP'
  }>
}

/**
 * Interface for server representation of workflow step
 */
interface WorkflowStepServer {
  processingType: string
  nofAllValue?: number | null
  taskTeamStepLabel?: {
    id: number
    text: string
  } | null
  taskTeams: Array<{
    user?: { id: number }
    group?: { id: number }
  }>
}

/**
 * Assemble FSI approvers data for server submission
 * Transforms UI approvers data into server-expected format
 *
 * Supports both string format ("USER-205", "GROUP-123") and object format
 * ({id: 205, systemHelperType: 'USER'}) for backward compatibility
 * Also handles approveActions if present
 *
 * @param typeData - Workflow type data from UI (flexible typing for dual format support)
 * @returns Formatted data for server
 */
export function assembleFsiApproversForServer(typeData: any): {
  approvers: {
    type: string
    steps: Array<{
      processingType: string
      nofAllValue?: number | null
      taskTeamStepLabel?: { id: number; text: string } | null
      taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
    }>
    approveActions: any[]
  }
} {
  const result = {
    approvers: {
      type: typeData.approvers?.type ?? 'NOT_DEFINED',
      steps: [] as Array<{
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      }>,
      approveActions: [] as any[]
    }
  }

  if (typeData.approvers?.steps) {
    for (const step of typeData.approvers.steps) {
      const approverObj: {
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      } = {
        processingType: step.processingType,
        nofAllValue: step.processingType === 'N_OF_ALL' ? (step.nofAllValue ?? null) : null,
        taskTeamStepLabel: step.taskTeamStepLabel
          ? {
              id: step.taskTeamStepLabel.value ?? step.taskTeamStepLabel.id ?? 0,
              text: step.taskTeamStepLabel.label ?? step.taskTeamStepLabel.text ?? ''
            }
          : null,
        taskTeams: []
      }

      // Transform merged array to taskTeams - supports both string and object formats
      if (step.merged && Array.isArray(step.merged)) {
        for (const item of step.merged) {
          // Handle string format like "USER-205" or "GROUP-123"
          if (typeof item === 'string') {
            const [type, id] = item.split('-')
            if (type === 'USER') {
              approverObj.taskTeams.push({ user: { id: parseInt(id, 10) } })
            } else if (type === 'GROUP') {
              approverObj.taskTeams.push({ group: { id: parseInt(id, 10) } })
            }
          }
          // Handle object format with systemHelperType
          else if (item.systemHelperType === 'USER') {
            approverObj.taskTeams.push({ user: { id: item.id } })
          } else if (item.systemHelperType === 'GROUP') {
            approverObj.taskTeams.push({ group: { id: item.id } })
          }
        }
      }

      result.approvers.steps.push(approverObj)
    }
  }

  return result
}

/**
 * Assemble FSI CR approvers data for server submission
 * Transforms UI workflow data into server-expected format
 *
 * Supports both string format ("USER-205", "GROUP-123") and object format
 * ({id: 205, systemHelperType: 'USER'}) for backward compatibility
 *
 * @param typeData - Workflow type data from UI (flexible typing for dual format support)
 * @returns Formatted data for server
 */
export function assembleFsiCRApproversForServer(typeData: any): {
  initialApprovers: {
    type: string
    steps: Array<{
      processingType: string
      nofAllValue?: number | null
      taskTeamStepLabel?: { id: number; text: string } | null
      taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
    }>
  }
} {
  const result = {
    initialApprovers: {
      type: typeData.initialApprovers?.type ?? 'NOT_DEFINED',
      steps: [] as Array<{
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      }>
    }
  }

  if (typeData.initialApprovers?.steps) {
    for (const step of typeData.initialApprovers.steps) {
      const approverObj: {
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      } = {
        processingType: step.processingType,
        nofAllValue: step.processingType === 'N_OF_ALL' ? (step.nofAllValue ?? null) : null,
        taskTeamStepLabel: step.taskTeamStepLabel
          ? {
              id: step.taskTeamStepLabel.value ?? step.taskTeamStepLabel.id ?? 0,
              text: step.taskTeamStepLabel.label ?? step.taskTeamStepLabel.text ?? ''
            }
          : null,
        taskTeams: []
      }

      // Transform merged array to taskTeams - supports both string and object formats
      if (step.merged && Array.isArray(step.merged)) {
        for (const item of step.merged) {
          // Handle string format like "USER-205" or "GROUP-123"
          if (typeof item === 'string') {
            const [type, id] = item.split('-')
            if (type === 'USER') {
              approverObj.taskTeams.push({ user: { id: parseInt(id, 10) } })
            } else if (type === 'GROUP') {
              approverObj.taskTeams.push({ group: { id: parseInt(id, 10) } })
            }
          }
          // Handle object format with systemHelperType
          else if (item.systemHelperType === 'USER') {
            approverObj.taskTeams.push({ user: { id: item.id } })
          } else if (item.systemHelperType === 'GROUP') {
            approverObj.taskTeams.push({ group: { id: item.id } })
          }
        }
      }

      result.initialApprovers.steps.push(approverObj)
    }
  }

  return result
}

/**
 * Assemble FSI reviewers data for server submission
 * Transforms UI reviewers data into server-expected format
 *
 * Supports both string format ("USER-205", "GROUP-123") and object format
 * ({id: 205, systemHelperType: 'USER'}) for backward compatibility
 *
 * @param typeData - Workflow type data from UI (flexible typing for dual format support)
 * @returns Formatted data for server
 */
export function assembleFsiReviewersForServer(typeData: any): {
  reviewers: {
    type: string
    steps: Array<{
      processingType: string
      nofAllValue?: number | null
      taskTeamStepLabel?: { id: number; text: string } | null
      taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
    }>
  }
} {
  const result = {
    reviewers: {
      type: typeData.reviewers?.type ?? 'NOT_DEFINED',
      steps: [] as Array<{
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      }>
    }
  }

  if (typeData.reviewers?.steps) {
    for (const step of typeData.reviewers.steps) {
      const reviewerObj: {
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      } = {
        processingType: step.processingType,
        nofAllValue: step.processingType === 'N_OF_ALL' ? (step.nofAllValue ?? null) : null,
        taskTeamStepLabel: step.taskTeamStepLabel
          ? {
              id: step.taskTeamStepLabel.value ?? step.taskTeamStepLabel.id ?? 0,
              text: step.taskTeamStepLabel.label ?? step.taskTeamStepLabel.text ?? ''
            }
          : null,
        taskTeams: []
      }

      // Transform merged array to taskTeams - supports both string and object formats
      if (step.merged && Array.isArray(step.merged)) {
        for (const item of step.merged) {
          // Handle string format like "USER-205" or "GROUP-123"
          if (typeof item === 'string') {
            const [type, id] = item.split('-')
            if (type === 'USER') {
              reviewerObj.taskTeams.push({ user: { id: parseInt(id, 10) } })
            } else if (type === 'GROUP') {
              reviewerObj.taskTeams.push({ group: { id: parseInt(id, 10) } })
            }
          }
          // Handle object format with systemHelperType
          else if (item.systemHelperType === 'USER') {
            reviewerObj.taskTeams.push({ user: { id: item.id } })
          } else if (item.systemHelperType === 'GROUP') {
            reviewerObj.taskTeams.push({ group: { id: item.id } })
          }
        }
      }

      result.reviewers.steps.push(reviewerObj)
    }
  }

  return result
}

/**
 * Assemble FSI editors data for server submission
 * Transforms UI collaborators data into server-expected format
 *
 * Supports both string format ("USER-205", "GROUP-123") and object format
 * ({id: 205, systemHelperType: 'USER'}) for backward compatibility
 *
 * @param typeData - Workflow type data from UI (flexible typing for dual format support)
 * @returns Formatted data for server
 */
export function assembleFsiEditorsForServer(typeData: any): {
  collaborators: {
    type: string
    steps: Array<{
      processingType: string
      nofAllValue?: number | null
      taskTeamStepLabel?: { id: number; text: string } | null
      taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
    }>
  }
} {
  const result = {
    collaborators: {
      type: typeData.collaborators?.type ?? 'NOT_DEFINED',
      steps: [] as Array<{
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      }>
    }
  }

  if (typeData.collaborators?.steps) {
    for (const step of typeData.collaborators.steps) {
      const editorObj: {
        processingType: string
        nofAllValue?: number | null
        taskTeamStepLabel?: { id: number; text: string } | null
        taskTeams: Array<{ user?: { id: number }; group?: { id: number } }>
      } = {
        processingType: step.processingType,
        nofAllValue: step.processingType === 'N_OF_ALL' ? (step.nofAllValue ?? null) : null,
        taskTeamStepLabel: step.taskTeamStepLabel
          ? {
              id: step.taskTeamStepLabel.value ?? step.taskTeamStepLabel.id ?? 0,
              text: step.taskTeamStepLabel.label ?? step.taskTeamStepLabel.text ?? ''
            }
          : null,
        taskTeams: []
      }

      // Transform merged array to taskTeams - supports both string and object formats
      if (step.merged && Array.isArray(step.merged)) {
        for (const item of step.merged) {
          // Handle string format like "USER-205" or "GROUP-123"
          if (typeof item === 'string') {
            const [type, id] = item.split('-')
            if (type === 'USER') {
              editorObj.taskTeams.push({ user: { id: parseInt(id, 10) } })
            } else if (type === 'GROUP') {
              editorObj.taskTeams.push({ group: { id: parseInt(id, 10) } })
            }
          }
          // Handle object format with systemHelperType
          else if (item.systemHelperType === 'USER') {
            editorObj.taskTeams.push({ user: { id: item.id } })
          } else if (item.systemHelperType === 'GROUP') {
            editorObj.taskTeams.push({ group: { id: item.id } })
          }
        }
      }

      result.collaborators.steps.push(editorObj)
    }
  }

  return result
}

export async function exportPropertySearch(filter: Record<string, any>, meta?: Record<string, any>): Promise<any> {
  // API expects { filter, meta, tag } in request body
  // 'tag' is passed through from filter.tag
  const reqObj: Record<string, any> = {
    filter,
    meta,
    tag: filter?.tag
  }
  return baseApiService.fetch<any>('/api/reports/propertiessearch', {
    method: 'POST',
    body: reqObj
  })
}

// Export duplicate document reports (migrated from legacy AngularJS)
export async function exportDuplicateDocumentReports(filter: Record<string, unknown>): Promise<unknown> {
  // Matches: apiWithLoader.one('api/reports/duplicatedocsreport/export').customPOST(filter);
  return baseApiService.fetch('/api/reports/duplicatedocsreport/export', {
    method: 'POST',
    body: filter ?? {}
  })
}
// ============================================================
// Rejection Reviewers
// ============================================================

/**
 * Response from rejection reviewers API endpoint
 */
export interface FileRejectionReviewersResponse {
  rejectionReviewers: {
    type: string
    steps: WorkflowStep[]
  }
  readOnly: boolean
  workflowTemplate: any
}

/**
 * Processed rejection reviewers with merged user/group data
 */
export interface FileRejectionReviewersProcessed {
  rejectionReviewers: {
    type: string
    steps: WorkflowStep[]
    members: Array<User | Group>
  }
  readOnly: boolean
  workflowTemplate: any
}

/**
 * Get FSI rejection reviewers with assembled user/group data
 *
 * Migrated from legacy AngularJS (api.files.js) getFsiRejectionReviewers.
 * This function has a unique structure where it populates a members array
 * from the first step's taskTeams (legacy behavior preserved exactly).
 *
 * @param fsiId - File/folder ID
 * @returns Rejection reviewers configuration with merged user/group data
 */
export async function getFsiRejectionReviewers(fsiId: number): Promise<FileRejectionReviewersProcessed> {
  // First get assembled users and groups
  const assembledData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = assembledData.merged

  // Then get FSI rejection reviewers (showLoader: false in original)
  const data = await baseApiService.fetch<FileRejectionReviewersResponse>(`/api/files/${fsiId}/rejectionreviewers`)

  // Initialize result with empty members array
  const result: FileRejectionReviewersProcessed = {
    rejectionReviewers: {
      type: data.rejectionReviewers.type,
      steps: data.rejectionReviewers.steps,
      members: []
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process first step's taskTeams if present (legacy behavior)
  if (data.rejectionReviewers.steps[0]) {
    for (const taskTeam of data.rejectionReviewers.steps[0].taskTeams) {
      // Match legacy: access .user.id directly without optional chaining (will throw if user is undefined)
      // This preserves exact legacy behavior where missing user would cause runtime error
      const id = taskTeam.group ? taskTeam.group.id : taskTeam.user!.id
      const systemHelperType = taskTeam.group ? 'GROUP' : 'USER'

      const found = availableUsersGroups.find(
        (item: User | Group) => item.id === id && (item as any).systemHelperType === systemHelperType
      )

      if (found) {
        result.rejectionReviewers.members.push(found)
      }
    }
  }

  return result
}

/**
 * Update FSI rejection reviewers (V2) - modernized version with template-based approach
 * @param id - File ID
 * @param tpl - Template containing rejection reviewers configuration
 * @param inherit - Whether to inherit settings
 * @param applyRecursively - Whether to apply recursively
 * @returns Promise<void>
 */
export async function updateFsiRejectionReviewersV2(
  id: number,
  tpl: FsiRejectionReviewersTemplate,
  inherit?: boolean,
  applyRecursively?: boolean
): Promise<void> {
  const reviewersObj = {
    rejectionReviewers: {
      steps: tpl.rejectionReviewers.usersGroups.length
        ? [
            {
              processingType: 'N_OF_ALL',
              nofAllValue: 1,
              taskTeams: [] as Array<{ group: { id: string } } | { user: { id: string } }>
            }
          ]
        : [],
      type: tpl.rejectionReviewers.type
    },
    inherit: inherit ? true : false,
    applyRecursively: applyRecursively ? true : false
  }

  // Process each user/group from the template (preserves exact legacy _.forEach behavior)
  _.forEach(tpl.rejectionReviewers.usersGroups, (stepItem: any) => {
    const stepItemArr = stepItem.split('-')
    reviewersObj.rejectionReviewers.steps[0].taskTeams.push(
      stepItemArr[0] === 'GROUP'
        ? {
            group: { id: stepItemArr[1] }
          }
        : {
            user: { id: stepItemArr[1] }
          }
    )
  })

  return baseApiService.fetch<void>(`/api/files/${id}/rejectionreviewers`, {
    method: 'PUT',
    body: reviewersObj
  })
}

/**
 * Get file trainees and merge with available users/groups data
 *
 * Fetches the trainee configuration for a file and enriches it with
 * available users/groups from the system. This is used to populate
 * trainee selection UI with full user/group details.
 *
 * @param fileId - The file ID
 * @returns Promise resolving to processed trainees data with merged users/groups
 */
export async function getFileTrainees(fileId: number): Promise<FileTraineesProcessed> {
  // First get available users and groups
  const usersGroupsData = await usersApiService.getAssembledUsersGroups()
  const availableUsersGroups = usersGroupsData.merged

  // Then fetch the file's trainee configuration
  const data = await baseApiService.fetch<FileTraineesResponse>(`/api/files/${fileId}/trainees`)

  // Build the result object
  const result: FileTraineesProcessed = {
    trainees: {
      createRAActionType: 'FOR_NOBODY',
      type: data.trainees.type,
      steps: data.trainees.steps
    },
    readOnly: data.readOnly,
    workflowTemplate: data.workflowTemplate
  }

  // Process taskTeams if first step exists
  if (data.trainees.steps && data.trainees.steps[0] && data.trainees.steps[0].taskTeams) {
    const members: Array<{ id: number; systemHelperType: 'USER' | 'GROUP' }> = []

    // Iterate through taskTeams and match with available users/groups
    for (const key in data.trainees.steps[0].taskTeams) {
      const taskTeam = data.trainees.steps[0].taskTeams[key]

      // Determine ID and systemHelperType based on group vs user
      let id: number
      let systemHelperType: 'USER' | 'GROUP'

      if (taskTeam.group) {
        id = taskTeam.group.id
        systemHelperType = 'GROUP'
      } else {
        id = taskTeam.user!.id
        systemHelperType = 'USER'
      }

      // Find matching user/group in available list
      const match = availableUsersGroups.find((item) => item.id === id && item.systemHelperType === systemHelperType)

      if (match) {
        members.push({
          id: match.id,
          systemHelperType: systemHelperType
        })
      }
    }

    // Add members array to first step
    result.trainees.steps[0].members = members
  }

  return result
}

/**
 * Update file trainees (V2 - simplified version)
 *
 * Updates the trainee configuration for a file. This version omits the 'members'
 * property from the trainees object before sending to the server.
 *
 * @param fileId - The file ID
 * @param data - Trainees data object containing trainees property
 * @param inherit - Whether to inherit trainees configuration (default: false)
 * @param applyRecursively - Whether to apply recursively to subfolders (default: false)
 * @returns Promise resolving when update is complete
 */
export async function updateFileTraineesV2(
  fileId: number,
  data: { trainees: any },
  inherit?: boolean,
  applyRecursively?: boolean
): Promise<void> {
  const traineesObj = {
    trainees: _.omit(data.trainees, 'members'),
    inherit: inherit ? true : false,
    applyRecursively: applyRecursively ? true : false
  }

  return baseApiService.fetch<void>(`/api/files/${fileId}/trainees`, {
    method: 'PUT',
    body: traineesObj
  })
}

/**
 * ============================================================================
 * PROPERTIES & METADATA APIs
 * ============================================================================
 */

/**
 * Get file metadata (properties)
 * Returns metadata elements for a specific file
 *
 * @param fileId - The file ID
 * @returns Promise with metadata elements
 */
export async function getFileMetadata(fileId: number): Promise<{ elements: MetadataElement[] }> {
  return baseApiService.fetch<{ elements: MetadataElement[] }>(`/api/v1/files/${fileId}/metadata`)
}

/**
 * Update file approvers (V2)
 * Migrated from legacy AngularJS (api.files.js) updateFileApproversV2.
 * @param fileId - File ID
 * @param data - Approvers data object containing approvers property
 * @param inherit - Whether to inherit approvers configuration (default: false)
 * @param applyRecursively - Whether to apply recursively to subfolders (default: false)
 * @returns Promise resolving when update is complete
 */
export async function updateFileApproversV2(
  fileId: number,
  data: { approvers: any },
  inherit?: boolean,
  applyRecursively?: boolean
): Promise<void> {
  const approversObj = {
    approvers: data.approvers,
    inherit: inherit ? true : false,
    applyRecursively: applyRecursively ? true : false
  }

  return baseApiService.fetch<void>(`/api/files/${fileId}/approvers`, {
    method: 'PUT',
    body: approversObj
  })
}

/**
 * Get signature forms list
 * @param filter - Optional filter parameters
 */
export async function getSignatureFormsList(filter?: Record<string, any>): Promise<any[]> {
  return baseApiService.fetch<any[]>('/api/files/signatureformslist', { params: filter })
}

/**
 * Fetch files for a given parent folder ID without version information.
 * @param parentId - The ID of the parent folder to fetch files from
 * @param filter - Optional filter parameters
 * @returns A promise resolving to an array of files
 */

export async function getFilesNoVersion(parentId: number, filter?: Record<string, any>): Promise<any[]> {
  return baseApiService.fetch<any[]>(`/api/files/${parentId}`, { params: filter })
}

/* Update file reviewers (V2)
 * Migrated from legacy AngularJS (api.files.js) updateFileReviewers.
 * @param fileId - File ID
 * @param data - Reviewers data object containing reviewers property
 * @param inherit - Whether to inherit reviewers configuration (default: false)
 * @param applyRecursively - Whether to apply recursively to subfolders (default: false)
 * @param noExtraModificationRequired - If true, skips transformation and sends reviewers as-is
 * @returns Promise resolving when update is complete
 */

export async function updateFileReviewers(
  fileId: number,
  data: { reviewers: any },
  inherit?: boolean,
  applyRecursively?: boolean,
  noExtraModificationRequired?: boolean
): Promise<void> {
  const reviewersObj = {
    reviewers: noExtraModificationRequired ? data.reviewers : assembleFsiReviewersForServer(data).reviewers,
    inherit: inherit ? true : false,
    applyRecursively: applyRecursively ? true : false
  }

  return baseApiService.fetch<void>(`/api/files/${fileId}/reviewers`, {
    method: 'PUT',
    body: reviewersObj
  })
}

/**
 * Update FSI editors (collaborators) for a file
 * Migrated from legacy AngularJS (api.files.js) updateFsiEditors.
 * @param fileId - File ID
 * @param editors - Editors data object containing collaborators property
 * @param applyRecursively - Whether to apply recursively to subfolders (default: false)
 * @param inherit - Whether to inherit editors configuration (default: false)
 * @param noExtraModificationRequired - If true, skips transformation and sends collaborators as-is
 * @returns Promise resolving when update is complete
 */
export async function updateFsiEditors(
  fileId: number,
  editors: { collaborators: any },
  applyRecursively?: boolean,
  inherit?: boolean,
  noExtraModificationRequired?: boolean
): Promise<void> {
  const editorsObj = {
    collaborators: noExtraModificationRequired
      ? editors.collaborators
      : assembleFsiEditorsForServer(editors).collaborators,
    inherit: inherit ? true : false,
    applyRecursively: applyRecursively ? true : false
  }

  return baseApiService.fetch<void>(`/api/files/${fileId}/collaborators`, {
    method: 'PUT',
    body: editorsObj
  })
}

/**
 * Update change request initial approvers for a file
 * Migrated from legacy AngularJS (api.files.js) updateFileCRApprovers.
 * @param fileId - File ID
 * @param data - Initial approvers data (with initialApprovers property)
 * @param inherit - Whether to inherit from parent
 * @param applyRecursively - Whether to apply recursively
 * @param noExtraModificationRequired - Whether to skip extra modification
 * @returns Promise<void>
 */
export async function updateFileCRApprovers(
  fileId: number,
  data: { initialApprovers: any },
  inherit?: boolean,
  applyRecursively?: boolean,
  noExtraModificationRequired?: boolean
): Promise<void> {
  // Assemble data for server submission
  const payload = {
    ...data,
    inherit: !!inherit,
    applyRecursively: !!applyRecursively,
    noExtraModificationRequired: !!noExtraModificationRequired
  }
  return baseApiService.fetch<void>(`/api/files/${fileId}/initialapprovers`, {
    method: 'PUT',
    body: payload
  })
}

/**
 * Get a shared link for a file (migrated from legacy AngularJS getFileShareLink).
 * @param fileId - File ID (number or string)
 * @param versionId - Version ID (number, optional)
 * @param minorVersionId - Minor version ID (number, optional)
 * @returns Promise resolving to the shared link object (type: any for parity)
 */
export async function getFileSharedLink(
  fileId: number | string,
  versionId?: number,
  minorVersionId?: number
): Promise<any> {
  // Build query object as in legacy: versionId >= 0 ? versionId : null, minorVersionId >= 0 ? minorVersionId : null
  const queryObj: Record<string, number | null> = {
    versionId: typeof versionId === 'number' && versionId >= 0 ? versionId : null,
    minorVersionId: typeof minorVersionId === 'number' && minorVersionId >= 0 ? minorVersionId : null
  }
  // Remove keys with null values to match legacy behavior
  Object.keys(queryObj).forEach((key) => {
    if (queryObj[key] == null) {
      delete queryObj[key]
    }
  })
  return baseApiService.fetch<any>(`/api/files/${fileId}/sharedlink`, {
    params: queryObj
  })
}

/**
 * Send a shared link email for a file (migrated from legacy AngularJS sendShareLinkEmail).
 * @param userIds - Array of user IDs to send to
 * @param subject - Email subject
 * @param uuid - File UUID (string)
 * @returns Promise resolving to the API response (type: any for parity)
 */
export async function sendSharedLinkEmail(userIds: (number | string)[], subject: string, uuid: string): Promise<any> {
  const data = {
    message: uuid,
    subject,
    userIdsTo: userIds
  }
  return baseApiService.fetch<any>('/api/emails/shareLink', {
    method: 'POST',
    body: data
  })
}

/**
 * Google Drive Integration
 */

interface GoogleDriveResponse {
  webLink: string
}

/**
 * Edit file in Google Docs/Sheets
 * Opens a new window and redirects to Google Drive edit interface
 * @param fileId - The ID of the file to edit
 */
export async function editInGoogleDocs(fileId: number): Promise<void> {
  const targetWindow = window.open('', '_blank')
  if (targetWindow) {
    targetWindow.document.write('Loading...')
  }

  try {
    const data = await baseApiService.fetch<GoogleDriveResponse>(`/api/gdrive/files/${fileId}`, {
      method: 'POST'
    })
    if (targetWindow && data.webLink) {
      targetWindow.location.href = data.webLink
    }
  } catch (error: any) {
    if (checkmateService.isCheckmated()) {
      // Checkmated: route through iMedidata SSO (matches legacy AngularJS behavior)
      const apiUrl = checkmateService.getApiUrl()
      const errorBody = error instanceof ApiError ? (error.body as any) : null
      const tenantFromLoginUrl = errorBody?.loginUrl
        ? getUrlParam('tenantUuid', decodeURIComponent(errorBody.loginUrl))
        : null
      const userId = getUser()?.id
      const tenant = tenantFromLoginUrl || getTenantUuid()
      const target = `${apiUrl}/api/gdrive/authorize?state=${fileId},${userId},${tenant}`

      try {
        const loginData = await authApiService.getIMedidataLoginUrl({ target })
        if (loginData.loginUrl && targetWindow) {
          targetWindow.location.href = loginData.loginUrl
        }
      } catch {
        // If getIMedidataLoginUrl fails, close the window
        targetWindow?.close()
      }
    } else {
      // Non-checkmated: direct redirect
      const userId = getUser()?.id
      const tenantUuid = getTenantUuid()
      const authUrl = `${baseApiService.getBaseUrl()}/api/gdrive/authorize?state=${fileId},${userId},${tenantUuid}`

      if (targetWindow) {
        targetWindow.location.href = authUrl
      }
    }
  }
}

/**
 * Upload file from Google Drive
 * @param fileId - The ID of the file to upload to
 */
export async function uploadFromGoogleDrive(fileId: number): Promise<any> {
  return baseApiService.fetch<any>(`/api/gdrive/files/${fileId}`)
}

/**
 * OneDrive/Office 365 Integration
 */

interface OneDriveResponse {
  webLink: string
}

/**
 * Edit file in Office 365
 * Opens a new window and redirects to Office 365 edit interface
 * @param fileId - The ID of the file to edit
 * @param fileExt - The file extension (used to check compatibility)
 */
export async function editInOffice365(fileId: number, fileExt?: string): Promise<void> {
  if (fileExt === 'doc') {
    throw new Error('EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED')
  }

  const targetWindow = window.open('', '_blank')
  if (targetWindow) {
    targetWindow.document.write('Loading...')
  }

  try {
    const data = await baseApiService.fetch<OneDriveResponse>(`/api/onedrive/files/${fileId}`, {
      method: 'POST'
    })
    if (targetWindow && data.webLink) {
      targetWindow.location.href = data.webLink
    }
  } catch (error: any) {
    if (checkmateService.isCheckmated()) {
      // Checkmated: route through iMedidata SSO (matches legacy AngularJS behavior)
      const apiUrl = checkmateService.getApiUrl()
      const errorBody = error instanceof ApiError ? (error.body as any) : null
      const tenantFromLoginUrl = errorBody?.loginUrl
        ? getUrlParam('tenantUuid', decodeURIComponent(errorBody.loginUrl))
        : null
      const userId = getUser()?.id
      const tenant = tenantFromLoginUrl || getTenantUuid()
      const target = `${apiUrl}/api/onedrive/authorize?state=${fileId},${userId},${tenant}`
      try {
        const loginData = await authApiService.getIMedidataLoginUrl({ target })
        if (loginData.loginUrl && targetWindow) {
          targetWindow.location.href = loginData.loginUrl
        }
      } catch {
        // If getIMedidataLoginUrl fails, close the window
        targetWindow?.close()
      }
    } else {
      // Non-checkmated: direct redirect
      const userId = getUser()?.id
      const tenantUuid = getTenantUuid()
      const authUrl = `${baseApiService.getBaseUrl()}/api/onedrive/authorize?state=${fileId},${userId},${tenantUuid}`

      if (targetWindow) {
        targetWindow.location.href = authUrl
      }
    }
  }
}

/**
 * Upload file from OneDrive
 * @param fileId - The ID of the file to upload to
 */
export async function uploadFromOneDrive(fileId: number): Promise<any> {
  return baseApiService.fetch<any>(`/api/onedrive/files/${fileId}`)
}

/**
 * Upload a file to a parent folder
 * @param file - The file to upload
 * @param parentId - The ID of the parent folder
 * @param validationCC - E-signature validation for certified copy
 * @param isTriggerChangeBlindingStatus - Whether to change blinding status
 * @param isTriggerChangeCertifiedCopy - Whether to mark as certified copy
 */
export async function uploadFile(
  file: File,
  parentId: number,
  validationCC?: any,
  isTriggerChangeBlindingStatus?: boolean,
  isTriggerChangeCertifiedCopy?: boolean
): Promise<any> {
  const formData = new FormData()
  formData.append('data', file)

  if (validationCC && isTriggerChangeCertifiedCopy) {
    formData.append('esignatureCC', JSON.stringify(validationCC))
  }

  if (isTriggerChangeBlindingStatus) {
    formData.append('isBlinded', 'false')
  }

  return baseApiService.fetch<any>('/api/files/singleFileUpload', {
    method: 'POST',
    params: { parent: parentId },
    body: formData
  })
}

/**
 * Legacy addFileToFavorite implementation (PATCH with favorite=true and optional favoriteName param)
 * @param fileId File ID to favorite
 * @param filename Optional favorite name
 */
export async function addFileToFavoriteLegacy(fileId: number | string, filename?: string): Promise<void> {
  // Matches: PATCH /api/files/{fileId}/favorite?favorite=true[&favoriteName=...]
  let url = `/api/files/${fileId}/favorite?favorite=true`
  if (filename) {
    url += `&favoriteName=${encodeURIComponent(filename)}`
  }
  return baseApiService.fetch<void>(url, {
    method: 'PATCH'
  })
}

// Helper to postprocess location info, matching legacy logic
export function postprocessLocationInfo(data: {
  url?: string
  s3Url?: string
  uuidToCheckState?: string
  message?: string
}): {
  url: string | null
  uuidToCheckState: string | null | undefined
  message?: string
} {
  const url = data.s3Url ? data.s3Url : data.url
  return {
    url: url ? url : null,
    uuidToCheckState: data.uuidToCheckState && !url ? data.uuidToCheckState : null,
    message: data.message
  }
}

export async function getPdfUrlInShowFile(
  fsiId: number,
  versionId?: number,
  minorVersionId?: number,
  params?: Record<string, unknown> | null
): Promise<{ url: string | null; uuidToCheckState: string | null | undefined; message?: string }> {
  // Validate file first
  const validation = await validateFile(fsiId)
  if (validation.value) {
    let resp: any
    if (typeof versionId === 'number' && versionId >= 0) {
      resp = await getFileVersionPdfDownloadUrl(fsiId, versionId, false, minorVersionId, false, params)
    } else {
      resp = await getFilePdfDownloadUrl(fsiId, false, false, params)
    }
    // The legacy code checked resp.status === 200, but fetch throws on error, so we assume success here
    // The legacy code used resp.data.plain(), but our API returns plain objects
    return postprocessLocationInfo(resp)
  } else {
    // Legacy: reject({ code: 7777 })
    // Here, throw an error with code 7777 for validation failure
    const error: any = new Error('File validation failed')
    error.code = 7777
    throw error
  }
}

/**
 * Upload a new version of an existing file
 * @param file - The file to upload
 * @param fileId - The ID of the file to create a new version for
 * @param validationCC - E-signature validation for certified copy
 * @param isTriggerChangeBlindingStatus - Whether to change blinding status
 * @param isTriggerChangeCertifiedCopy - Whether to mark as certified copy
 */
export async function uploadFileVersion(
  file: File,
  fileId: number,
  validationCC?: any,
  isTriggerChangeBlindingStatus?: boolean,
  isTriggerChangeCertifiedCopy?: boolean
): Promise<any> {
  const formData = new FormData()
  formData.append('data', file)

  if (validationCC && isTriggerChangeCertifiedCopy) {
    formData.append('esignatureCC', JSON.stringify(validationCC))
  }

  if (isTriggerChangeBlindingStatus) {
    formData.append('isBlinded', 'false')
  }

  return baseApiService.fetch<any>(`/api/files/${fileId}/uploadVersion`, {
    method: 'POST',
    body: formData
  })
}

/**
 * Upload version from existing RCM document
 * Used for selecting an existing document from RCM to create a new version
 * @param sourceFsiId - The ID of the source file to copy from
 * @param targetFsiId - The ID of the target file/folder to upload to
 */
export async function uploadVersionFromExistingDocument(sourceFsiId: number, targetFsiId: number): Promise<any> {
  return baseApiService.fetch<any>('/api/files/uploadVersionFromExistingDocument', {
    method: 'POST',
    body: {
      sourceFsiId,
      targetFsiId
    }
  })
}

/**
 * Get file diff to SOP (Standard Operating Procedure)
 * Used to generate a comparison between current file and SOP template
 * @param fileId - The ID of the file to compare
 * @param fullCompare - Whether to show detailed comparison (true) or overview (false)
 * @returns Response containing PDF location info for the diff viewer
 */
export async function getFileDiffToSop(fileId: number, fullCompare: boolean = false): Promise<any> {
  const params: Record<string, string> = {}
  if (fullCompare) {
    params.fullCompare = 'true'
  }
  return baseApiService.fetch<any>(`/api/files/${fileId}/difftosop`, { params })
}

/**
 * Update QC document status
 * Used in Quality Control review workflow to mark documents as PASSED or FAILED
 * @param payload - QC status update payload containing fsiId, status, and optional rejection details
 * @returns Updated file information
 */
export async function updateQCDocStatus(payload: import('./types').QCDocStatusUpdate): Promise<any> {
  return baseApiService.fetch<any>('/api/v2/files/qcdocstatus', {
    method: 'PATCH',
    body: payload
  })
}
/* Validate file content for live content verification
 * Checks if file content matches expected format and is valid
 * @param fileId - The ID of the file to validate
 * @param validation - Optional e-signature validation data
 * @returns Response with validation result (value: true/false)
 */
export async function getFileValidation(fileId: number, validation?: any): Promise<{ value: boolean }> {
  const requestObj = validation || {}
  return baseApiService.fetch<{ value: boolean }>(`/api/files/${fileId}/validate`, {
    params: requestObj
  })
}

/**
 * Regenerate document rendition (PDF conversion)
 * Resets conversion status and triggers re-rendering of document PDF
 * @param fileId - Single file ID or array of file IDs to regenerate
 * @returns Response with operation status
 */
export async function regenerateDocumentRendition(fileId: number | number[]): Promise<any> {
  const requestObj = {
    ids: Array.isArray(fileId) ? fileId : [fileId]
  }
  return baseApiService.fetch<any>('/api/files/resetconversionstatus', {
    method: 'PATCH',
    body: requestObj
  })
}

/**
 * E-signature validation result interface
 * Matches the structure from useEsignature hook
 */
export interface ESignatureValidation {
  username?: string
  password?: string
  relayState?: string
  result?: any
}

/**
 * Options for changeFilesDocType operation
 * Supports both individual file operations and batch operations
 */
export interface ChangeDocTypeOptions {
  selectedWholeResult?: boolean
  isPropertiesSearch?: boolean
  meta?: any
  filter?: any
  parentFolderId?: number
}

/**
 * Change document type for one or multiple files
 * Supports three operation modes:
 * 1. Property search batch operation (searchable metadata criteria)
 * 2. Folder-wide batch operation (all files in folder matching filter)
 * 3. Multi-file operation (specific file IDs)
 *
 * @param filesId - Array of file IDs to change document type
 * @param docTypeId - ID of the new document type to apply
 * @param validation - Optional e-signature validation data
 * @param options - Operation options (batch vs individual, search criteria)
 * @returns Promise that resolves when operation completes
 *
 * @example
 * // Change doc type for specific files
 * await changeFilesDocType([123, 456], 789, validation)
 *
 * @example
 * // Change doc type for all files in folder matching filter
 * await changeFilesDocType([], 789, validation, {
 *   selectedWholeResult: true,
 *   parentFolderId: 100,
 *   filter: { status: 'DRAFT' }
 * })
 */
export async function changeFilesDocType(
  filesId: number[],
  docTypeId: number,
  validation?: ESignatureValidation,
  options?: ChangeDocTypeOptions
): Promise<any> {
  const requestObj: any = {
    esignature: validation,
    ids: filesId,
    newDocType: docTypeId
  }

  // Handle batch operations (selectedWholeResult = true)
  if (options?.selectedWholeResult) {
    // Property search batch operation
    if (options.isPropertiesSearch && options.meta && options.filter?.metadataCriteria) {
      const filter = { ...options.filter, workflowIsNull: true }
      return baseApiService.fetch('/api/files/propertysearch/setdoctypeall', {
        method: 'PATCH',
        body: {
          propertySearchRequestDTO: { filter },
          operationDTO: {
            ids: filesId,
            newDocType: docTypeId,
            esignatureDTO: validation,
            esignature: null
          }
        }
      })
    }
    // Folder-wide batch operation
    else if (options.parentFolderId) {
      return baseApiService.fetch(`/api/files/${options.parentFolderId}/setdoctypeall`, {
        method: 'PATCH',
        body: {
          ids: filesId,
          newDocType: docTypeId,
          esignature: validation,
          filter: options.filter,
          esignatureDTO: validation
        }
      })
    }
  }

  // Standard multi-file operation
  return baseApiService.fetch('/api/files/multiSetDocType', {
    method: 'PATCH',
    body: requestObj
  })
}

/**
 * Get file diff comparison PDF URL
 * Parity with legacy ApiFiles.getFileDiffAgainst
 * @param fileId - First file ID
 * @param versionId - First file version ID
 * @param fileToCompareId - Second file ID
 * @param versionToCompareId - Second file version ID
 * @param minorVersionId - Minor version ID for first file (optional)
 * @param minorVersionId2 - Minor version ID for second file (optional)
 * @param fullCompare - Whether to show detailed comparison (optional, default false)
 * @returns Promise resolving to PDF location info
 */
export async function getFileDiffAgainst(
  fileId: number,
  versionId: number,
  fileToCompareId: number,
  versionToCompareId: number,
  minorVersionId?: number,
  minorVersionId2?: number,
  fullCompare?: boolean
): Promise<{ location?: string; s3Location?: string; uuid?: string }> {
  const params: Record<string, any> = {}

  if (minorVersionId !== undefined && minorVersionId !== null) {
    params.minorVersionId = minorVersionId
  }

  if (minorVersionId2 !== undefined && minorVersionId2 !== null) {
    params.minorVersionId2 = minorVersionId2
  }

  if (fullCompare === true) {
    params.fullCompare = 'true' // Legacy sends as string 'true'
  }

  return baseApiService.fetch<{ location?: string; s3Location?: string; uuid?: string }>(
    `/api/files/${fileId}/${versionId}/diff/${fileToCompareId}/${versionToCompareId}`,
    { params }
  )
}
